<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick, inject, onErrorCaptured } from 'vue';
import type { WritableComputedRef } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { contactsApi } from '../api/contactsApi';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import type { AddressInfo } from '../types/contact.types';
import { contactDetailMyResidentialEditingKey } from '../contactDetailInjectionKeys';
import {
    applyCountryChangeClearsSubLocation,
    buildMyLocalAddressRecordFromForm,
    MY_MALAYSIA_COUNTRY_CODE,
    resolveTotalCodeFromField,
    resolveCountryTotalCode,
    countryCodeToSearchLabel,
    shouldShowMyLocationFieldsWithAddDefault,
    normalizeMyLocationRowForForm,
    normalizeMyLocationFieldForPersist,
    resolvePostcodeTotalCodeForForm,
    resolvePostcodeDisplayNameFromOptions,
    clearMyLocationPlaceholdersOnAddress,
    sanitizeMyLocationValueForForm,
    isMyLocationPlaceholderValue,
    formatMyAddressRowBodyForDisplay,
    formatMyResidentialSummaryForDisplay,
    isMyCorporateBranchAddressEntityMapped,
    ensureMalaysiaCountryWhenMyLocationPresent,
} from '../utils/contactMyLocationFields';
import ContactAddressSelectDialog from './ContactAddressSelectDialog.vue';
import ContactBranchAddressShowDialog from './ContactBranchAddressShowDialog.vue';
import MyBranchAddressDisplayLine from './MyBranchAddressDisplayLine.vue';
import ContactResidentialBranchRowEditor from './ContactResidentialBranchRowEditor.vue';
import ContactAddressConsolidateDialog from './ContactAddressConsolidateDialog.vue';
import type { ContactAddressCandidate } from '../types/contact.types';
import { mapCandidateToMyAddressData } from '../utils/contactAddressSelect';
import {
    applyBranchAddressRowToDraft,
    buildAllCorporateBranchShowItems,
    type BranchAddressShowItem,
} from '../utils/contactBranchAddressShow';
import {
    ensureCachedMyCountryAndState,
    fetchCachedMyAllPostcodeOptions,
} from '../utils/myLocationOptionsCache';
import { useMyCorporateBranchAddressDelete } from '../composables/useMyCorporateBranchAddressDelete';
import type { MyRegisterAddressDeleteContext } from '@/features/register/MY/utils/persistMyRegisterContactAddresses';
import SyncFieldIndicator from '@features/Entity_my/components/SyncFieldIndicator.vue';
import {
    shouldPersistMyAddressDelete,
    type MyCorporateBranchAddressLists,
} from '../utils/persistMyCorporateBranchAddresses';

type AddressType = 'B07001' | 'B07002'; // 'B07001' = Local Address, 'B07002' = Foreign Address

interface AddressModel {
    addressType?: AddressType | null;
    localAddress?: AddressInfo | null;
    foreignAddress?: AddressInfo | null;
    [key: string]: any;
}

interface Props {
    modelValue?: AddressModel | null;
    readonly?: boolean;
    isIndividual?: boolean; // Individual Detail인지 여부
    /** Contact detail — Select Address popup */
    contactCd?: string;
    /** Entity_my: MAP 저장·Show Address repoint 컨텍스트 */
    entityCd?: string;
    /** MY Corporate: L_CONTACT_BRANCH 기반 Residential 목록 */
    useBranchAddresses?: boolean;
    residentialAddressList?: any[];
    /** Entity_my Show Address: contact 전체 branch 풀 (카드 목록과 분리) */
    residentialAddressShowPool?: any[];
    /** Show Address 팝업 — Service/Business branch 목록 */
    serviceAddressListForShow?: any[];
    businessAddressListForShow?: any[];
    showResidentialMultiHint?: boolean;
    residentialMultiHintText?: string;
    onReSyncAddressClick?: () => void;
    /** 삭제 PUT 시 Service/Business 목록 포함 */
    resolveBranchListsForPersist?: (residentialList: any[]) => MyCorporateBranchAddressLists;
    /** Registrable Create: MY 주소 watch emit 루프 방지 (Save 시 getAddressModelForSave) */
    suppressParentEmit?: boolean;
    /** Contact List / Registrable — Residential·Registered 대신 "Address" 라벨 */
    simpleAddressLabel?: boolean;
    /** Contact List / Registrable — 다건 힌트·Select Address·Address Consolidation 숨김 */
    hideAddressConsolidationUi?: boolean;
    /** Contact List Individual — L_CONTACT_ADDRESS 다건 UI (branch UI 재사용, 저장 경로는 flat) */
    contactListFlatAddressMode?: boolean;
    /** Entity Detail — branch 주소 Delete(−) 숨김 */
    hideAddressDelete?: boolean;
    /** MY Register — 역할별 주소 삭제 시 Register API 사용 */
    registerAddressDeleteContext?: MyRegisterAddressDeleteContext | null;
    /** Entity officer detail — SG sync field indicators */
    syncChanges?: Map<string, { type: 'UPDATE' | 'CONFLICT'; before: unknown; after: unknown; note?: string }> | null;
    /** BizFile(A06001) — 우편번호 검색(돋보기)만 숨기고 postal 필드는 readonly 표시. 나머지 필드/폼은 기존 로직 그대로 */
    hidePostalSearch?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    readonly: false,
    isIndividual: false,
    useBranchAddresses: false,
    residentialAddressList: () => [],
    residentialAddressShowPool: () => [],
    serviceAddressListForShow: () => [],
    businessAddressListForShow: () => [],
    showResidentialMultiHint: false,
    residentialMultiHintText: 'Multiple residential addresses exist for this contact.',
    suppressParentEmit: false,
    simpleAddressLabel: false,
    hideAddressConsolidationUi: false,
    contactListFlatAddressMode: false,
    hideAddressDelete: false,
    registerAddressDeleteContext: null,
    syncChanges: null,
    hidePostalSearch: false,
});

function syncChange(...fieldNames: string[]) {
    if (!props.syncChanges) return null;
    for (const f of fieldNames) {
        const hit = props.syncChanges.get(f);
        if (hit) return hit;
    }
    return null;
}

const emit = defineEmits<{
    'update:modelValue': [value: AddressModel];
    'update:residentialAddressList': [value: any[]];
    refresh: [];
}>();

const { isDeleting, confirmAndDeleteBranchAddress } = useMyCorporateBranchAddressDelete({
    getContactCd: () => String(props.contactCd || '').trim(),
    getRegisterAddressDeleteContext: () => props.registerAddressDeleteContext ?? null,
    getContactListFlatAddressMode: () => !!props.contactListFlatAddressMode,
    onRefreshed: async () => {
        emit('refresh');
    },
});

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);
const residentialAddressLabel = computed(() => {
    if (props.simpleAddressLabel) {
        return t('contact.Address') || 'Address';
    }
    return props.isIndividual
        ? (t('contact.Residential Address') || 'Residential Address')
        : (t('contact.Registered Address') || 'Registered Address');
});
const showResidentialMultiHintEffective = computed(
    () => props.showResidentialMultiHint && !props.hideAddressConsolidationUi,
);

// 세션의 jurisdiction 확인 (SG가 아니면 block, street, building 입력 가능)
const sessionStore = useSessionStore();
const isJurisdictionSG = computed(() => {
  // sessionStore.user를 직접 참조하여 반응형으로 작동하도록 수정
  const customerInfo = sessionStore.user?.customer || null;
  return customerInfo?.jurisdiction === 'SG';
});

// 세션의 JURISDICTION이 MY인지 확인
// A06002 (Manual)일 때도 말레이시아 주소 필드를 표시 (CODE_NAME → TOTAL_CODE 변환 필요)
const isJurisdictionMY = computed(() => {
  // sessionStore.user를 직접 참조하여 반응형으로 작동하도록 수정
  const customerInfo = sessionStore.user?.customer || null;
  const jurisdiction = customerInfo?.jurisdiction || '';
  const isMY = jurisdiction?.toUpperCase() === 'MY' || jurisdiction?.toUpperCase() === 'MALAYSIA';
  console.log('🔍 [ContactAddressForm] isJurisdictionMY check:', {
    jurisdiction,
    isMY,
    customerInfo
  });
  return isMY;
});

// A06002 (Manual)인지 확인
const isManualRegistrant = computed(() => {
  const registerType = (props.modelValue as any)?.CONTACT_REGISTER_TYPE || (props.modelValue as any)?.contactRegisterType || '';
  return String(registerType).trim().toUpperCase() === 'A06002';
});

/** MY location 옵션 — branch draft·watch(immediate)·computed보다 먼저 선언 (TDZ 방지) */
const countryOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
const stateOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
const cityOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
const postcodeOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
/** 전국 Postcode 캐시 — City별 목록과 병합·역검색용 (ContactInfoForm altPostcodeAllOptionsCache와 동일 역할) */
const postcodeAllOptionsCache = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);

const mergedResidentialPostcodeAutocompleteItems = computed(() => {
    const map = new Map<string, { TOTAL_CODE: string; CODE_NAME: string }>();
    postcodeAllOptionsCache.value.forEach((o) => map.set(o.TOTAL_CODE, o));
    postcodeOptions.value.forEach((o) => map.set(o.TOTAL_CODE, o));
    return Array.from(map.values());
});

const residentialPostcodeReverseSearchItems = computed(() => {
    const map = new Map<string, { TOTAL_CODE: string; CODE_NAME: string }>();
    postcodeAllOptionsCache.value.forEach((o) => map.set(o.TOTAL_CODE, o));
    return Array.from(map.values());
});

function getResidentialPostcodeComboItems(): Array<{ TOTAL_CODE: string; CODE_NAME: string }> {
    const cityRaw = myAddressData.value.city;
    const cityCode =
        resolveTotalCodeFromField(cityRaw, cityOptions.value) || String(cityRaw || '').trim();
    if (cityCode) {
        return postcodeOptions.value.length > 0
            ? postcodeOptions.value
            : mergedResidentialPostcodeAutocompleteItems.value;
    }
    return residentialPostcodeReverseSearchItems.value;
}

/** MY Corporate branch Registered — ContactInfoForm Service/Business와 동일 콤보 규칙 */
function getBranchResidentialPostcodeComboItems(address: any) {
    const cityRaw = address?.city;
    const cityCode =
        resolveTotalCodeFromField(cityRaw, cityOptions.value) || String(cityRaw || '').trim();
    if (cityCode) {
        return postcodeOptions.value.length > 0
            ? postcodeOptions.value
            : mergedResidentialPostcodeAutocompleteItems.value;
    }
    return residentialPostcodeReverseSearchItems.value;
}

async function ensureResidentialPostcodeFullCacheForReverse() {
    if (postcodeAllOptionsCache.value.length > 0) return;
    await loadAllPostcodeOptions();
}

/** Residential Edit 진입 시 Service/Business hydrateContactJsonAddressFields와 동일 */
async function hydrateResidentialMyAddressFields() {
    if (!isJurisdictionMY.value) return;
    await loadCountryOptions();
    await loadStateOptions();
    const stateCode =
        resolveTotalCodeFromField(myAddressData.value.state, stateOptions.value) ||
        String(myAddressData.value.state || '').trim();
    if (stateCode) {
        await loadCitiesByState(stateCode);
    }
    const cityCode =
        resolveTotalCodeFromField(myAddressData.value.city, cityOptions.value) ||
        String(myAddressData.value.city || '').trim();
    if (cityCode) {
        await loadPostcodeOptions(String(cityCode).trim(), true).catch(() => {});
    }
    await loadAllPostcodeOptions();
}

// MY 옵션 로드 함수 (재사용 가능하도록 분리)
const loadMyOptions = async () => {
    if (props.suppressParentEmit) {
        await loadCountryOptions();
        await loadStateOptions();
        return;
    }
    console.log('✅ [ContactAddressForm] MY jurisdiction detected, loading options...');
    
    // addressData에서 초기값 설정
    if (addressData.value.localAddress) {
        const localAddr = addressData.value.localAddress;
        
        // readonly: 분기 유지 — 표시용 옵션 로드 + 바인딩은 countryCode 등 우선(비-readonly와 동일)
        if (readonly.value) {
            console.log('🔍 [ContactAddressForm] loadMyOptions readonly - localAddr:', localAddr);
            await loadCountryOptions();
            await loadStateOptions();
            const la = localAddr as any;
            const countryCode = la.countryCode || localAddr.country || '';
            const stateCode = la.stateCode || localAddr.state || '';
            const cityCode = la.cityCode || localAddr.city || '';
            const postcodeCode =
                la.postcodeCode || localAddr.post_code || localAddr.postcode || localAddr.postalCode || '';
            if (stateCode) {
                await loadCitiesByState(stateCode);
            }
            if (cityCode && String(cityCode).trim()) {
                await loadPostcodeOptions(String(cityCode).trim(), true).catch(() => {});
            }
            myAddressData.value = {
                addressLine1: localAddr.addressLine1 || '',
                addressLine2: localAddr.addressLine2 || '',
                addressLine3: localAddr.addressLine3 || '',
                country: countryCode,
                state: stateCode,
                city: cityCode,
                postcode: postcodeCode,
            };
            console.log('✅ [ContactAddressForm] Initial MY address data set (readonly):', {
                myAddressData: myAddressData.value,
                localAddress: localAddr,
                postcode: myAddressData.value.postcode,
                post_code_from_localAddr: localAddr.post_code
            });
        } else {
            // readonly가 아닐 때는 옵션 로드 및 순차 로직 수행
            await loadCountryOptions();
            await loadStateOptions();
            
            // A06002 (Manual)인지 확인
            const registerType = (props.modelValue as any)?.CONTACT_REGISTER_TYPE || (props.modelValue as any)?.contactRegisterType || '';
            const isManual = String(registerType).trim().toUpperCase() === 'A06002';
            
            // 백엔드에서 역추적한 GUID 값을 우선 사용
            // residential_address에 이미 GUID가 포함되어 있으면 사용, 없으면 역추적
            // Entity_my와 동일한 패턴: postcodeCode || postcode (텍스트 값도 확인)
            
            let countryCode = (localAddr as any).countryCode || '';
            let stateCode = (localAddr as any).stateCode || '';
            let cityCode = (localAddr as any).cityCode || '';
            // Postcode GUID 값 추출 (역추적이 아닌 저장된 GUID 값 사용)
            let postcodeCode = (localAddr as any).postcodeCode || '';
            
            // GUID 값이 없으면 텍스트 값을 사용 (역추적을 위해)
            const countryName = countryCode ? '' : (localAddr.country || '');
            const stateName = stateCode ? '' : (localAddr.state || '');
            const cityName = cityCode ? '' : (localAddr.city || '');
            const postcodeName = postcodeCode ? '' : (localAddr.postcode || localAddr.postalCode || (localAddr as any).post_code || '');
            
            if (isManual) {
                // A06002일 때: CODE_NAME → TOTAL_CODE 변환
                // countryCode, stateCode 등이 없고 country, state 등이 CODE_NAME 형태로 있을 때 변환
                console.log('🔍 [ContactAddressForm] A06002 detected, converting CODE_NAME to TOTAL_CODE:', {
                    country: localAddr.country,
                    state: localAddr.state,
                    city: localAddr.city,
                    postcode: localAddr.postcode || localAddr.post_code,
                    countryCode: localAddr.countryCode,
                    stateCode: localAddr.stateCode
                });
                
                // Country: B15 공통코드에서 CODE_NAME으로 TOTAL_CODE 찾기
                // countryCode가 없고 country가 CODE_NAME 형태일 때만 변환
                if (localAddr.country && !countryCode) {
                    const countryMatch = countryOptions.value.find(opt => 
                        opt.CODE_NAME === localAddr.country || opt.CODE_NAME?.toUpperCase() === String(localAddr.country).toUpperCase()
                    );
                    if (countryMatch) {
                        countryCode = countryMatch.TOTAL_CODE;
                        console.log('✅ [ContactAddressForm] Country CODE_NAME → TOTAL_CODE:', localAddr.country, '→', countryCode);
                    } else {
                        // 매칭되지 않으면 원본 값 사용 (나중에 옵션에 추가될 수 있음)
                        countryCode = localAddr.country;
                        console.log('⚠️ [ContactAddressForm] Country 매칭 실패, 원본 값 사용:', localAddr.country);
                    }
                } else if (localAddr.country && !countryCode) {
                    // countryCode가 없으면 country 값을 그대로 사용
                    countryCode = localAddr.country;
                }
                
                // State: stateOptions에서 CODE_NAME으로 TOTAL_CODE 찾기
                if (localAddr.state && !stateCode) {
                    const stateMatch = stateOptions.value.find(opt => 
                        opt.CODE_NAME === localAddr.state || opt.CODE_NAME?.toUpperCase() === String(localAddr.state).toUpperCase()
                    );
                    if (stateMatch) {
                        stateCode = stateMatch.TOTAL_CODE;
                        console.log('✅ [ContactAddressForm] State CODE_NAME → TOTAL_CODE:', localAddr.state, '→', stateCode);
                        // State가 찾아지면 City 목록 로드
                        await loadCitiesByState(stateCode);
                    } else {
                        // 매칭되지 않으면 원본 값 사용
                        stateCode = localAddr.state;
                        console.log('⚠️ [ContactAddressForm] State 매칭 실패, 원본 값 사용:', localAddr.state);
                    }
                } else if (localAddr.state && !stateCode) {
                    stateCode = localAddr.state;
                }
                
                // City: cityOptions에서 CODE_NAME으로 TOTAL_CODE 찾기
                if (localAddr.city && !cityCode) {
                    const cityMatch = cityOptions.value.find(opt => 
                        opt.CODE_NAME === localAddr.city || opt.CODE_NAME?.toUpperCase() === String(localAddr.city).toUpperCase()
                    );
                    if (cityMatch) {
                        cityCode = cityMatch.TOTAL_CODE;
                        console.log('✅ [ContactAddressForm] City CODE_NAME → TOTAL_CODE:', localAddr.city, '→', cityCode);
                        // City가 찾아지면 Postcode 목록 로드
                        await loadPostcodeOptions(cityCode, true).catch(() => {});
                    } else {
                        // 매칭되지 않으면 원본 값 사용
                        cityCode = localAddr.city;
                        console.log('⚠️ [ContactAddressForm] City 매칭 실패, 원본 값 사용:', localAddr.city);
                    }
                } else if (localAddr.city && !cityCode) {
                    cityCode = localAddr.city;
                }
                
                // Postcode: postcodeOptions에서 CODE_NAME으로 TOTAL_CODE 찾기
                // Postcode는 City가 있어야 정확한 역추적이 가능하므로 City가 있을 때만 수행
                if (postcodeName && !postcodeCode && cityCode) {
                    // Postcode 옵션이 로드되지 않았으면 먼저 로드
                    if (postcodeOptions.value.length === 0) {
                        await loadPostcodeOptions(cityCode, true).catch(() => {});
                    }
                    const postcodeMatch = postcodeOptions.value.find(opt => 
                        opt.CODE_NAME === postcodeName || opt.CODE_NAME?.toUpperCase() === String(postcodeName).toUpperCase()
                    );
                    if (postcodeMatch) {
                        postcodeCode = postcodeMatch.TOTAL_CODE;
                        console.log('✅ [ContactAddressForm] Postcode CODE_NAME → TOTAL_CODE:', postcodeName, '→', postcodeCode);
                    } else {
                        // 매칭되지 않으면 원본 값 사용
                        postcodeCode = postcodeName;
                        console.log('⚠️ [ContactAddressForm] Postcode 매칭 실패, 원본 값 사용:', postcodeName);
                    }
                } else if (postcodeName && !postcodeCode) {
                    postcodeCode = postcodeName;
                }
            } else {
                countryCode = resolveTotalCodeFromField(
                    localAddr.countryCode || localAddr.country,
                    countryOptions.value
                );
                stateCode = resolveTotalCodeFromField(
                    localAddr.stateCode || localAddr.state,
                    stateOptions.value
                );
                cityCode = resolveTotalCodeFromField(
                    localAddr.cityCode || localAddr.city,
                    cityOptions.value
                );
                postcodeCode = localAddr.postcodeCode || localAddr.postcode || localAddr.post_code || '';
            }
            
            // Country가 있으면 countryOptions에 추가 (없는 경우)
            if (countryCode) {
                const countryExists = countryOptions.value.some(opt => opt.TOTAL_CODE === countryCode);
                if (!countryExists) {
                    countryOptions.value.push({
                        TOTAL_CODE: String(countryCode).trim(),
                        CODE_NAME: String(localAddr.country || countryCode).trim().toUpperCase()
                    });
                }
            }
            
            // State가 있으면 City 목록 로드 (Entity_my 패턴)
            if (stateCode && !isManual) {
                await loadCitiesByState(stateCode);
            }
            
            // City가 있으면 City 옵션에 추가 및 Postcode 목록 로드
            if (cityCode && cityCode.trim()) {
                const cityExists = cityOptions.value.some(opt => opt.TOTAL_CODE === cityCode);
                if (!cityExists) {
                    cityOptions.value.push({
                        TOTAL_CODE: String(cityCode).trim(),
                        CODE_NAME: String(localAddr.city || cityCode).trim()
                    });
                    // City 이름 조회 시도
                    entityMyApi.getMalaysiaCities(undefined, cityCode)
                        .then((cityResponse: any) => {
                            const cityData = cityResponse.data?.data || cityResponse.data;
                            const cityInfo = Array.isArray(cityData) ? cityData[0] : (cityData || {});
                            if (cityInfo?.name) {
                                const cityIndex = cityOptions.value.findIndex(opt => opt.TOTAL_CODE === cityCode);
                                if (cityIndex >= 0) {
                                    cityOptions.value[cityIndex].CODE_NAME = String(cityInfo.name).trim();
                                }
                            }
                        })
                        .catch(() => {});
                }
                // City가 있으면 Postcode 목록 로드 (Entity_my 패턴: City 로드 후 Postcode 목록 먼저 로드)
                // A06001과 A06002 모두 Postcode 목록 로드
                await loadPostcodeOptions(cityCode, true).catch(() => {
                    console.log('ℹ️ [ContactAddressForm] Postcode 목록 로드 실패');
                });
            }
            
            // Postcode 값 바인딩 (Entity_my 패턴: postcodeCode가 있으면 무조건 옵션에 추가)
            console.log('🔍 [ContactAddressForm] Postcode 바인딩 체크:', {
                postcodeCode,
                postcodeCodeType: typeof postcodeCode,
                postcodeCodeLength: postcodeCode?.length,
                postcodeOptionsBefore: postcodeOptions.value.length,
                postcodeName,
                cityCode
            });
            
            // Entity_my 패턴: postcodeCode가 있으면 무조건 옵션에 추가
            if (postcodeCode && postcodeCode.trim()) {
                // Postcode가 옵션에 있는지 확인
                let postcodeExists = postcodeOptions.value.some(opt => opt.TOTAL_CODE === postcodeCode);
                console.log('🔍 [ContactAddressForm] Postcode 존재 여부 (첫 확인):', postcodeExists);
                
                // City의 Postcode 목록이 로드되지 않았으면 먼저 로드 시도
                if (!postcodeExists && cityCode) {
                    console.log('🔍 [ContactAddressForm] Postcode 옵션에 없음, City의 Postcode 목록 로드 시도');
                    await loadPostcodeOptions(cityCode, true).catch(() => {});
                    // 다시 확인
                    postcodeExists = postcodeOptions.value.some(opt => opt.TOTAL_CODE === postcodeCode);
                    console.log('🔍 [ContactAddressForm] Postcode 존재 여부 (로드 후):', postcodeExists);
                }
                
                // 여전히 없으면 수동으로 추가
                if (!postcodeExists) {
                    // Entity_my 패턴: postcodeCode를 CODE_NAME으로도 사용 (postcodeName이 있으면 우선 사용)
                    // postcodeName이 없으면 Postcode 목록에서 찾기 시도
                    let postcodeDisplayName = postcodeName;
                    if (!postcodeDisplayName) {
                        // 전체 Postcode 목록에서 찾기
                        if (postcodeAllOptionsCache.value.length === 0) {
                            await loadAllPostcodeOptions();
                        }
                        const postcodeMatch = mergedResidentialPostcodeAutocompleteItems.value.find(
                            (opt) => opt.TOTAL_CODE === postcodeCode
                        );
                        if (postcodeMatch) {
                            postcodeDisplayName = postcodeMatch.CODE_NAME;
                            console.log('✅ [ContactAddressForm] Postcode 옵션에서 이름 찾음:', postcodeDisplayName);
                        } else {
                            // 찾지 못하면 GUID 값 자체를 사용하지 않고 빈 문자열 사용
                            postcodeDisplayName = '';
                            console.warn('⚠️ [ContactAddressForm] Postcode 이름을 찾을 수 없음, 빈 문자열 사용');
                        }
                    }
                    
                    // Postcode 옵션에 추가
                    postcodeOptions.value.push({
                        TOTAL_CODE: String(postcodeCode).trim(),
                        CODE_NAME: postcodeDisplayName || String(postcodeCode).trim() // 최후의 수단으로 GUID 사용
                    });
                    console.log('✅ [ContactAddressForm] Postcode 추가됨:', {
                        TOTAL_CODE: postcodeCode,
                        CODE_NAME: postcodeDisplayName || String(postcodeCode).trim(),
                        postcodeOptionsLength: postcodeOptions.value.length
                    });
                } else {
                    console.log('ℹ️ [ContactAddressForm] Postcode 이미 존재:', postcodeCode);
                }
            } else {
                console.log('⚠️ [ContactAddressForm] postcodeCode가 없거나 비어있음');
                
                // GUID가 없고 텍스트 값만 있는 경우, Postcode 목록에서 찾기 시도
                if (postcodeName) {
                    // City가 있으면 해당 City의 Postcode 목록에서 찾기, 없으면 전체 목록에서 찾기
                    if (cityCode) {
                        // City의 Postcode 목록이 로드되지 않았으면 로드
                        if (postcodeOptions.value.length === 0) {
                            await loadPostcodeOptions(cityCode, true).catch(() => {});
                        }
                    } else {
                        // City가 없으면 전체 Postcode 목록에서 찾기
                        if (postcodeAllOptionsCache.value.length === 0) {
                            await loadAllPostcodeOptions();
                        }
                    }
                    
                    const postcodeMatch = mergedResidentialPostcodeAutocompleteItems.value.find(
                        (opt) =>
                            opt.CODE_NAME === postcodeName ||
                            opt.CODE_NAME?.toUpperCase() === String(postcodeName).toUpperCase()
                    );
                    if (postcodeMatch) {
                        postcodeCode = postcodeMatch.TOTAL_CODE;
                        console.log('✅ [ContactAddressForm] Postcode 목록에서 찾음:', postcodeName, '→', postcodeCode);
                    } else {
                        console.warn('⚠️ [ContactAddressForm] Postcode 역추적 실패:', postcodeName, 'cityCode:', cityCode);
                    }
                }
            }
            
            // Postcode 바인딩 전 최종 확인 및 옵션에 추가 (반드시 바인딩 전에 옵션에 있어야 함)
            if (postcodeCode && postcodeCode.trim()) {
                let finalPostcodeExists = postcodeOptions.value.some(opt => opt.TOTAL_CODE === postcodeCode);
                console.log('🔍 [ContactAddressForm] Postcode 바인딩 전 최종 확인:', {
                    postcodeCode,
                    existsInOptions: finalPostcodeExists,
                    optionsLength: postcodeOptions.value.length
                });
                
                if (!finalPostcodeExists) {
                    console.warn('⚠️ [ContactAddressForm] Postcode가 옵션에 없음, 바인딩 전 강제 추가');
                    // Postcode 이름 찾기 시도
                    let postcodeDisplayName = postcodeName;
                    if (!postcodeDisplayName) {
                        // 전체 Postcode 목록에서 찾기
                        if (postcodeAllOptionsCache.value.length === 0) {
                            await loadAllPostcodeOptions();
                        }
                        const postcodeMatch = mergedResidentialPostcodeAutocompleteItems.value.find(
                            (opt) => opt.TOTAL_CODE === postcodeCode
                        );
                        if (postcodeMatch) {
                            postcodeDisplayName = postcodeMatch.CODE_NAME;
                        }
                    }
                    
                    postcodeOptions.value.push({
                        TOTAL_CODE: String(postcodeCode).trim(),
                        CODE_NAME: postcodeDisplayName || String(postcodeCode).trim()
                    });
                    console.log('✅ [ContactAddressForm] Postcode 옵션에 추가 완료 (바인딩 전):', {
                        TOTAL_CODE: postcodeCode,
                        CODE_NAME: postcodeDisplayName || String(postcodeCode).trim()
                    });
                    
                    // 다시 확인
                    finalPostcodeExists = postcodeOptions.value.some(opt => opt.TOTAL_CODE === postcodeCode);
                    console.log('🔍 [ContactAddressForm] Postcode 옵션 추가 후 재확인:', finalPostcodeExists);
                }
            }
            
            // 초기 로드 플래그 설정 (watch가 값을 초기화하지 않도록 - Entity_my 패턴)
            // 반드시 바인딩 전에 설정해야 함
            isInitializingLocation.value = true;
            
            // countryCode, stateCode, cityCode, postcodeCode를 우선 사용하여 바인딩
            // street01, street02, street03를 addressLine1, addressLine2, addressLine3로 매핑
            // GUID 값이 있으면 GUID 사용, 없으면 빈 문자열 (텍스트 값은 사용하지 않음)
            
            console.log('🔍 [ContactAddressForm] ====== 최종 바인딩 전 GUID 값 확인 ======');
            console.log('🔍 [ContactAddressForm] countryCode (최종):', countryCode, '타입:', typeof countryCode);
            console.log('🔍 [ContactAddressForm] stateCode (최종):', stateCode, '타입:', typeof stateCode);
            console.log('🔍 [ContactAddressForm] cityCode (최종):', cityCode, '타입:', typeof cityCode);
            console.log('🔍 [ContactAddressForm] postcodeCode (최종):', postcodeCode, '타입:', typeof postcodeCode);
            console.log('🔍 [ContactAddressForm] postcodeCode 길이:', postcodeCode?.length);
            console.log('🔍 [ContactAddressForm] postcodeCode가 GUID 형식인가?', postcodeCode && postcodeCode.length > 30);
            console.log('🔍 [ContactAddressForm] postcodeCode가 옵션에 있는가?', postcodeCode ? postcodeOptions.value.some(opt => opt.TOTAL_CODE === postcodeCode) : false);
            
            // street01, street02, street03를 addressLine1, addressLine2, addressLine3로 매핑
            const addressLine1 = localAddr.addressLine1 || (localAddr as any).street01 || '';
            const addressLine2 = localAddr.addressLine2 || (localAddr as any).street02 || '';
            const addressLine3 = localAddr.addressLine3 || (localAddr as any).street03 || '';
            
            myAddressData.value = {
                addressLine1: addressLine1,
                addressLine2: addressLine2,
                addressLine3: addressLine3,
                country: countryCode, // GUID 값만 사용
                state: stateCode, // GUID 값만 사용
                city: cityCode, // GUID 값만 사용
                postcode: postcodeCode, // GUID 값만 사용
            };
            
            console.log('✅ [ContactAddressForm] ====== 최종 myAddressData 바인딩 완료 ======');
            console.log('✅ [ContactAddressForm] myAddressData.value:', myAddressData.value);
            console.log('✅ [ContactAddressForm] country (GUID):', myAddressData.value.country);
            console.log('✅ [ContactAddressForm] state (GUID):', myAddressData.value.state);
            console.log('✅ [ContactAddressForm] city (GUID):', myAddressData.value.city);
            console.log('✅ [ContactAddressForm] postcode (GUID):', myAddressData.value.postcode);
            console.log('✅ [ContactAddressForm] postcode가 옵션에 있는가?', myAddressData.value.postcode ? postcodeOptions.value.some(opt => opt.TOTAL_CODE === myAddressData.value.postcode) : false);
            
            // DOM 업데이트 대기
            await nextTick();
            
            // Postcode 바인딩 후 최종 확인
            if (myAddressData.value.postcode && myAddressData.value.postcode.trim()) {
                const postcodeInOptions = postcodeOptions.value.some(opt => opt.TOTAL_CODE === myAddressData.value.postcode);
                console.log('🔍 [ContactAddressForm] Postcode 바인딩 후 최종 확인:', {
                    postcode: myAddressData.value.postcode,
                    existsInOptions: postcodeInOptions,
                    optionsLength: postcodeOptions.value.length,
                    firstFewOptions: postcodeOptions.value.slice(0, 3).map(opt => ({ TOTAL_CODE: opt.TOTAL_CODE, CODE_NAME: opt.CODE_NAME }))
                });
                
                if (!postcodeInOptions) {
                    console.error('❌ [ContactAddressForm] Postcode가 옵션에 없음! 강제 추가 시도');
                    postcodeOptions.value.push({
                        TOTAL_CODE: String(myAddressData.value.postcode).trim(),
                        CODE_NAME: postcodeName || String(myAddressData.value.postcode).trim()
                    });
                }
            }
            
            console.log('✅ [ContactAddressForm] Initial MY address data set:', {
                myAddressData: myAddressData.value,
                localAddress: localAddr,
                postcodeOptionsLength: postcodeOptions.value.length,
                postcodeInOptions: myAddressData.value.postcode ? postcodeOptions.value.some(opt => opt.TOTAL_CODE === myAddressData.value.postcode) : false,
                isManual
            });
            
            // 초기 로드 완료 후 플래그 해제 (watch가 다시 실행되도록)
            // setTimeout을 사용하여 모든 watch가 완료된 후 해제
            await nextTick();
            setTimeout(() => {
                isInitializingLocation.value = false;
                console.log('✅ [ContactAddressForm] isInitializingLocation 플래그 해제');
            }, 200); // 200ms로 증가하여 모든 watch가 완료되도록
        }
    }
};

// readonly 변수 정의 (템플릿에서 사용)
const readonly = computed(() => props.readonly);

// 필드별 readonly 계산
const isFieldReadonly = (fieldPath: string) => {
    return props.readonly;
};

// MY 한 줄 요약·읽기 전용 필드: Service(alt)와 같이 TOTAL_CODE → CODE_NAME 옵션 조회 후, 없으면 JSON 텍스트
const getCountryDisplayName = computed(() => {
    const code = String(myAddressData.value.country || '').trim();
    if (code) {
        const option = countryOptions.value.find((opt) => opt.TOTAL_CODE === code);
        if (option) return String(option.CODE_NAME || '').toUpperCase();
    }
    const originalValue = String(props.modelValue?.localAddress?.country || '').trim();
    if (originalValue) return originalValue.toUpperCase();
    return code.toUpperCase();
});

const getStateDisplayName = computed(() => {
    const code = String(myAddressData.value.state || '').trim();
    if (code) {
        const option = stateOptions.value.find((opt) => opt.TOTAL_CODE === code);
        if (option) return option.CODE_NAME;
    }
    const originalValue = String(props.modelValue?.localAddress?.state || '').trim();
    if (originalValue) return originalValue;
    return code;
});

const getCityDisplayName = computed(() => {
    const code = String(myAddressData.value.city || '').trim();
    if (code) {
        const option = cityOptions.value.find((opt) => opt.TOTAL_CODE === code);
        if (option) return option.CODE_NAME;
    }
    const originalValue = String(props.modelValue?.localAddress?.city || '').trim();
    if (originalValue) return originalValue;
    return code;
});

const getPostcodeDisplayName = computed(() => {
    const code = String(myAddressData.value.postcode || '').trim();
    if (code) {
        const option = mergedResidentialPostcodeAutocompleteItems.value.find(
            (opt) => opt.TOTAL_CODE === code
        );
        if (option) return option.CODE_NAME;
    }
    const modelLocalAddr = props.modelValue?.localAddress;
    const originalValue = String(
        (modelLocalAddr as any)?.post_code ||
            modelLocalAddr?.postcode ||
            modelLocalAddr?.postalCode ||
            ''
    ).trim();
    if (originalValue) return originalValue;
    return code;
});

const isMyAddressEditing =
    inject(contactDetailMyResidentialEditingKey, undefined) ?? ref(false);
/** MY + 주소 없음: Business처럼 + Add 후 필드 표시 */
const residentialMyExpanded = ref(false);
/** + Add 직후 country 선택 전 State/City/Postcode 기본 표시 */
const residentialShowLocationOnAdd = ref(false);
const canEditMyAddress = computed(
    () =>
        (isJurisdictionMY.value || props.useBranchAddresses || props.contactListFlatAddressMode) &&
        !readonly.value,
);

/** MY branch(Corporate Detail) 또는 Contact List Individual flat — 다건 Residential UI */
const useMultiResidentialListUi = computed(
    () => !!props.useBranchAddresses || !!props.contactListFlatAddressMode,
);

/** MY branch(Corporate Detail) — 세션 jurisdiction과 무관하게 Registered 블록 표시 */
const showMyCorporateBranchAddressUi = computed(
    () => isJurisdictionMY.value || useMultiResidentialListUi.value,
);

/** MY Contact List — Address 섹션 접기 미사용(항상 표시, 고객 요청) */
const useContactListAddressSectionCollapse = computed(() => false);
const addressSectionExpanded = ref(false);
const isAddressSectionCollapsed = computed(
    () => useContactListAddressSectionCollapse.value && !addressSectionExpanded.value,
);

watch(
    () => props.contactCd,
    () => {
        addressSectionExpanded.value = false;
    },
);

function toggleAddressSectionExpanded() {
    if (!useContactListAddressSectionCollapse.value) return;
    addressSectionExpanded.value = !addressSectionExpanded.value;
}

const formatBranchResidentialDisplay = (address: any): string =>
    formatMyAddressRowBodyForDisplay(address, {
        countryOpts: countryOptions.value,
        stateOpts: stateOptions.value,
        cityOpts: cityOptions.value,
        postcodeOpts: mergedResidentialPostcodeAutocompleteItems.value,
    });

const branchRowEditorLabels = computed(() => ({
    showAddress: t('contact.Show Address') || 'Show Address',
    edit: t('contact.Edit') || 'Edit',
    cancel: t('contact.Cancel') || 'Cancel',
    delete: t('contact.Delete') || 'Delete',
    alias: t('contact.Alias') || 'Alias',
    line1: t('contact.Address Line 1') || 'Address Line 1',
    line2: t('contact.Address Line 2') || 'Address Line 2',
    line3: t('contact.Address Line 3') || 'Address Line 3',
    country: t('contact.Country') || 'Country',
    state: t('contact.State') || 'State',
    city: t('contact.City') || 'City',
    postcode: t('contact.Postcode') || 'Postcode',
    pleaseSelect: t('contact.Please Select') || 'Please Select',
}));

const residentialBranchDraftList = ref<any[]>([]);
const residentialBranchEditState = ref<Record<string, boolean>>({});
const isInitializingBranchResidentialLocation = ref(false);
/** Postcode→State/City 역채움 중에는 State/City 변경 cascade(하위 필드 비우기) 금지 */
const suppressBranchLocationCascade = ref(false);
const lastResidentialBranchListKey = ref<string | null>(null);
/** props → draft 동기화 중 emit 금지 (로드 시 Maximum recursive updates 방지) */
const isSyncingBranchDraftFromProps = ref(false);

/** MY Corporate branch / Contact List flat — L_CONTACT_BRANCH·L_CONTACT_ADDRESS 다건 블록 */
const showBranchResidentialSection = computed(() => useMultiResidentialListUi.value);

/** draft·props·Show Pool(L_CONTACT_BRANCH_MAP) 기준 주소 존재 여부 */
const branchResidentialListHasContent = computed(() => {
    if (residentialBranchDraftList.value.length > 0) return true;
    const list = props.residentialAddressList || [];
    if (list.length > 0) return true;
    const pool = props.residentialAddressShowPool || [];
    if (pool.length > 0) return true;
    if (pool.some((row) => isMyCorporateBranchAddressEntityMapped(row))) return true;
    return false;
});

const showBranchResidentialEmptyPlaceholder = computed(
    () =>
        showBranchResidentialSection.value &&
        !branchResidentialListHasContent.value &&
        !props.hideAddressConsolidationUi,
);

function branchPostcodeOptionsForResolve() {
    return postcodeOptions.value.length > 0
        ? postcodeOptions.value
        : mergedResidentialPostcodeAutocompleteItems.value;
}

function ensureBranchPostcodeInOptions(postcodeCode: string, displayName?: string) {
    const code = String(postcodeCode || '').trim();
    if (!code || isMyLocationPlaceholderValue(code)) return;
    const pool = branchPostcodeOptionsForResolve();
    if (pool.some((o) => o.TOTAL_CODE === code)) return;
    const fromCache = mergedResidentialPostcodeAutocompleteItems.value.find(
        (o) => o.TOTAL_CODE === code,
    );
    const label =
        displayName ||
        fromCache?.CODE_NAME ||
        resolvePostcodeDisplayNameFromOptions(code, pool) ||
        code;
    postcodeOptions.value.push({
        TOTAL_CODE: code,
        CODE_NAME: label,
    });
}

function resolveBranchPostcodeTotalCode(address: any) {
    const src = address || {};
    return resolvePostcodeTotalCodeForForm(
        src.post_code ?? src.postcode ?? src.postalCode,
        src.postcodeCode,
        branchPostcodeOptionsForResolve(),
    );
}

function isMalaysiaBranchAddressRow(address: any) {
    if (!address) return false;
    ensureMalaysiaCountryWhenMyLocationPresent(address, {
        countryOpts: countryOptions.value,
        stateOpts: stateOptions.value,
    });
    if (address.country === MY_MALAYSIA_COUNTRY_CODE) {
        address.countryCode = MY_MALAYSIA_COUNTRY_CODE;
        address._countrySearch = countryCodeToSearchLabel(
            address.country,
            countryOptions.value,
        );
    }
    const code = resolveCountryTotalCode(address?.country, countryOptions.value);
    return code === MY_MALAYSIA_COUNTRY_CODE;
}

/** Service/Business mapMyJsonRowForForm과 동일 — country 필드 우선(countryCode는 country 비었을 때만) */
const mapBranchResidentialRowForForm = (address: any) => {
    const src = address || {};
    clearMyLocationPlaceholdersOnAddress(src);
    const postcodeOpts = branchPostcodeOptionsForResolve();
    const prepared = {
        ...src,
        country:
            sanitizeMyLocationValueForForm(src.country) ||
            sanitizeMyLocationValueForForm(src.countryCode),
        state:
            sanitizeMyLocationValueForForm(src.state) ||
            sanitizeMyLocationValueForForm(src.stateCode),
        city:
            sanitizeMyLocationValueForForm(src.city) ||
            sanitizeMyLocationValueForForm(src.cityCode),
        post_code: resolvePostcodeTotalCodeForForm(
            src.post_code ?? src.postcode ?? src.postalCode,
            src.postcodeCode,
            postcodeOpts,
        ),
    };
    const row = normalizeMyLocationRowForForm(prepared, {
        countryOpts: countryOptions.value,
        stateOpts: stateOptions.value,
        cityOpts: cityOptions.value,
    });
    if (row.country) {
        row.countryCode = row.country;
    }
    row._countrySearch = countryCodeToSearchLabel(row.country, countryOptions.value);
    row.alias = String(row.alias ?? row.ALIAS ?? '').trim();
    return row;
};

function remapResidentialBranchDraftInPlace() {
    if (!props.useBranchAddresses || residentialBranchDraftList.value.length === 0) return;
    isSyncingBranchDraftFromProps.value = true;
    try {
        residentialBranchDraftList.value = residentialBranchDraftList.value.map((row) =>
            mapBranchResidentialRowForForm(row),
        );
    } finally {
        nextTick(() => {
            isSyncingBranchDraftFromProps.value = false;
        });
    }
}

/** Service hydrateContactJsonAddressFields와 동일 — City/Postcode 옵션만 로드, country 덮어쓰지 않음 */
async function hydrateBranchResidentialRowFields(address: any) {
    if (!props.useBranchAddresses || !address) return;
    if (!isMalaysiaBranchAddressRow(address)) return;
    isInitializingBranchResidentialLocation.value = true;
    try {
        // State가 표시명(JOHOR)이어도 GUID로 해석 후 City 목록 로드
        const stateCode =
            resolveTotalCodeFromField(address.state, stateOptions.value) ||
            String(address.state || '').trim();
        if (!stateCode || isMyLocationPlaceholderValue(stateCode)) return;
        // 폼 모델도 GUID로 정규화 (표시명은 autocomplete에 안 맞음)
        const resolvedGuid = resolveTotalCodeFromField(stateCode, stateOptions.value);
        if (resolvedGuid && resolvedGuid !== address.state) {
            address.state = resolvedGuid;
        }
        await loadCitiesByState(stateCode);

        let cityCode = resolveTotalCodeFromField(address.city, cityOptions.value);
        // City 비어 있고 Postcode만 있으면 역검색으로 State/City 채움
        if (!cityCode) {
            const postcodeRaw = String(
                address.post_code || address.postcode || address.postalCode || '',
            ).trim();
            if (postcodeRaw && !isMyLocationPlaceholderValue(postcodeRaw)) {
                if (postcodeAllOptionsCache.value.length === 0) {
                    await loadAllPostcodeOptions();
                }
                const postcodeNumber =
                    mergedResidentialPostcodeAutocompleteItems.value.find(
                        (opt) => opt.TOTAL_CODE === postcodeRaw,
                    )?.CODE_NAME || postcodeRaw;
                const locationInfo = await findStateAndCityByPostcode(
                    postcodeNumber,
                    resolvedGuid || stateCode,
                    null,
                );
                if (locationInfo?.stateCode) {
                    address.state = locationInfo.stateCode;
                    await loadCitiesByState(locationInfo.stateCode);
                }
                if (locationInfo?.cityCode) {
                    address.city = locationInfo.cityCode;
                    cityCode = locationInfo.cityCode;
                }
            }
        }

        if (cityCode) {
            await loadPostcodeOptions(cityCode, true);
        }
    } finally {
        setTimeout(() => {
            isInitializingBranchResidentialLocation.value = false;
        }, 100);
    }
}

async function hydrateAllMalaysiaBranchResidentialRows() {
    if (!props.useBranchAddresses || residentialBranchDraftList.value.length === 0) return;
    isSyncingBranchDraftFromProps.value = true;
    try {
        for (const row of residentialBranchDraftList.value) {
            if (!row) continue;
            const countryCode = resolveCountryTotalCode(row.country, countryOptions.value);
            if (countryCode !== MY_MALAYSIA_COUNTRY_CODE) continue;
            clearMyLocationPlaceholdersOnAddress(row);
            // eslint-disable-next-line no-await-in-loop
            await hydrateBranchResidentialRowFields(row);
        }
    } finally {
        nextTick(() => {
            isSyncingBranchDraftFromProps.value = false;
        });
    }
}

function residentialBranchListSyncKey(list: any[]) {
    const cd = String(props.contactCd || '').trim();
    return `${cd}|${(list || []).length}|${JSON.stringify(
        (list || []).map((a) => ({
            s1: a?.street01 || a?.addressLine1,
            s2: a?.street02 || a?.addressLine2,
            pc: a?.post_code || a?.postcode,
            c: a?.country,
            st: a?.state,
            ci: a?.city,
            en: a?.entity_name,
            al: a?.alias ?? a?.ALIAS,
        })),
    )}`;
}

const getResidentialBranchEditKey = (index: number) => `branch-res-${index}`;

const isResidentialBranchEditing = (index: number) =>
    !!residentialBranchEditState.value[getResidentialBranchEditKey(index)];

const anyResidentialBranchEditing = () =>
    Object.values(residentialBranchEditState.value).some(Boolean);

function syncResidentialBranchDraftFromProps(force = false) {
    if (!useMultiResidentialListUi.value) return;
    if (!force && anyResidentialBranchEditing()) return;
    const src = props.residentialAddressList || [];
    const key = residentialBranchListSyncKey(src);
    if (!force && key === lastResidentialBranchListKey.value) return;
    lastResidentialBranchListKey.value = key;
    isSyncingBranchDraftFromProps.value = true;
    try {
        const mapped = src.map((row) => mapBranchResidentialRowForForm(row));
        const current = residentialBranchDraftList.value;
        const extraRows =
            !force && current.length > mapped.length
                ? current.slice(mapped.length).map((row) => mapBranchResidentialRowForForm(row))
                : [];
        residentialBranchDraftList.value = [...mapped, ...extraRows];
        if (force) {
            residentialBranchEditState.value = {};
        }
    } finally {
        nextTick(() => {
            isSyncingBranchDraftFromProps.value = false;
            if (countryOptions.value.length > 0 && stateOptions.value.length > 0) {
                void hydrateAllMalaysiaBranchResidentialRows().catch((err) =>
                    console.error('[ContactAddressForm] hydrate branch rows from props', err),
                );
            }
        });
    }
}

watch(
    () => props.residentialAddressList,
    () => {
        if (!useMultiResidentialListUi.value) return;
        syncResidentialBranchDraftFromProps();
    },
    { immediate: true, deep: true },
);

watch(
    () => props.contactCd,
    () => {
        if (!useMultiResidentialListUi.value) return;
        lastResidentialBranchListKey.value = null;
        syncResidentialBranchDraftFromProps(true);
        void loadBranchMyDisplayOptions();
    },
);

/** country/state 옵션 로드 후 draft — stateCode·GUID remap */
watch(
    () => countryOptions.value.length,
    (len, prevLen) => {
        if (!props.useBranchAddresses || len === 0) return;
        if (prevLen === len && len > 0) return;
        remapResidentialBranchDraftInPlace();
    },
);

watch(
    () => stateOptions.value.length,
    (len, prevLen) => {
        if (!props.useBranchAddresses || len === 0) return;
        if (prevLen === len && len > 0) return;
        remapResidentialBranchDraftInPlace();
        void hydrateAllMalaysiaBranchResidentialRows().catch((err) =>
            console.error('[ContactAddressForm] hydrate after state options', err),
        );
    },
);

/** Service/Business serviceRowShowLocationFields와 동일 — draft country 변경에 즉시 반응 */
const branchResidentialRowShowLocationFields = computed(() =>
    residentialBranchDraftList.value.map((row) =>
        shouldShowMyLocationFieldsWithAddDefault(
            row?.country,
            countryOptions.value,
            !!row?._showLocationDefaultsOnAdd,
        ),
    ),
);

const showBranchResidentialShowDialog = ref(false);
const branchResidentialShowEditIndex = ref<number | null>(null);
const branchResidentialShowItems = ref<BranchAddressShowItem[]>([]);

const corporateBranchShowLabels = computed(() => ({
    residential: residentialAddressLabel.value,
    service: t('contact.Service Address') || 'Service Address',
    business: t('contact.Business Address') || 'Business Address',
}));

const entityCdForBranch = computed(() => String(props.entityCd || '').trim());

const residentialRowsForShowAddress = computed(() => {
    const pool = props.residentialAddressShowPool;
    if (entityCdForBranch.value && Array.isArray(pool) && pool.length > 0) {
        return pool;
    }
    return Array.isArray(props.residentialAddressList) ? props.residentialAddressList : [];
});

const openBranchResidentialShowAddress = (index: number) => {
    if (!props.useBranchAddresses || !canEditMyAddress.value || !isResidentialBranchEditing(index)) return;
    branchResidentialShowEditIndex.value = index;
    branchResidentialShowItems.value = buildAllCorporateBranchShowItems({
        residential: residentialRowsForShowAddress.value,
        service: Array.isArray(props.serviceAddressListForShow) ? props.serviceAddressListForShow : [],
        business: Array.isArray(props.businessAddressListForShow) ? props.businessAddressListForShow : [],
        labels: corporateBranchShowLabels.value,
        formatResidential: (row) => formatBranchResidentialDisplay(row),
    });
    showBranchResidentialShowDialog.value = true;
};

const onBranchResidentialShowSelected = async (row: Record<string, unknown>) => {
    const index = branchResidentialShowEditIndex.value;
    if (index == null || index < 0) return;
    const key = getResidentialBranchEditKey(index);
    if (!residentialBranchEditState.value[key]) {
        residentialBranchEditState.value = {
            ...residentialBranchEditState.value,
            [key]: true,
        };
    }
    await loadCountryOptions();
    await loadStateOptions();
    const draft = residentialBranchDraftList.value[index];
    if (!draft) return;
    const applyOpts = entityCdForBranch.value
        ? { repointEntityMapBranch: true, entityCd: entityCdForBranch.value }
        : { preserveDraftBranchIdentity: true };
    applyBranchAddressRowToDraft(
        draft,
        row,
        {
            countryOpts: countryOptions.value,
            stateOpts: stateOptions.value,
            cityOpts: cityOptions.value,
        },
        applyOpts,
    );
    draft._countrySearch = countryCodeToSearchLabel(draft.country, countryOptions.value);
    Object.assign(draft, mapBranchResidentialRowForForm(draft));
    await hydrateBranchResidentialRowFields(draft);
};

function createEmptyBranchResidentialRow() {
    return {
        alias: '',
        street01: '',
        street02: '',
        street03: '',
        country: MY_MALAYSIA_COUNTRY_CODE,
        state: '',
        city: '',
        post_code: '',
        _countrySearch: '',
        _showLocationDefaultsOnAdd: true,
    };
}

const addBranchResidentialRow = async () => {
    if (!canEditMyAddress.value || !useMultiResidentialListUi.value) return;
    await loadBranchMyDisplayOptions();
    await loadCountryOptions();
    await loadStateOptions();
    await loadAllPostcodeOptions();
    const mapped = mapBranchResidentialRowForForm(createEmptyBranchResidentialRow());
    const newIndex = residentialBranchDraftList.value.length;
    residentialBranchDraftList.value = [...residentialBranchDraftList.value, mapped];
    residentialBranchEditState.value = {
        ...residentialBranchEditState.value,
        [getResidentialBranchEditKey(newIndex)]: true,
    };
    if (useContactListAddressSectionCollapse.value) {
        addressSectionExpanded.value = true;
    }
};

const canDeleteBranchResidentialRow = (_address?: any) => canEditMyAddress.value;

const removeBranchResidentialRow = async (index: number) => {
    if (!canEditMyAddress.value || !useMultiResidentialListUi.value) return;
    const addr = residentialBranchDraftList.value[index];
    if (!canDeleteBranchResidentialRow(addr)) return;

    const newList = residentialBranchDraftList.value.filter((_, i) => i !== index);
    const lists = props.resolveBranchListsForPersist
        ? props.resolveBranchListsForPersist(newList)
        : {
              residentialAddressList: newList.map((r) => ({ ...r })),
              serviceAddressList: [],
              businessAddressList: [],
          };

    const persistOnDelete = shouldPersistMyAddressDelete({
        registerAddressDeleteContext: props.registerAddressDeleteContext,
        contactCd: props.contactCd,
        useBranchAddresses: props.useBranchAddresses,
        contactListFlatAddressMode: props.contactListFlatAddressMode,
        row: addr,
    });
    const ok = await confirmAndDeleteBranchAddress(
        `residential-${index}`,
        lists,
        persistOnDelete,
    );
    if (!ok) return;

    residentialBranchDraftList.value = newList;
    const nextEdit: Record<string, boolean> = {};
    Object.entries(residentialBranchEditState.value).forEach(([k, v]) => {
        if (!k.startsWith('branch-res-')) nextEdit[k] = v;
    });
    residentialBranchEditState.value = nextEdit;
    emitResidentialBranchDraft();
};

const toggleResidentialBranchEdit = async (index: number) => {
    if (!canEditMyAddress.value) return;
    const key = getResidentialBranchEditKey(index);
    const next = !residentialBranchEditState.value[key];
    residentialBranchEditState.value = { ...residentialBranchEditState.value, [key]: next };
    if (next) {
        const row = residentialBranchDraftList.value[index];
        if (row) {
            Object.assign(row, mapBranchResidentialRowForForm(row));
            await hydrateBranchResidentialRowFields(row);
        }
    } else {
        const src = props.residentialAddressList || [];
        if (src[index]) {
            isSyncingBranchDraftFromProps.value = true;
            try {
                residentialBranchDraftList.value[index] = mapBranchResidentialRowForForm(src[index]);
            } finally {
                nextTick(() => {
                    isSyncingBranchDraftFromProps.value = false;
                });
            }
        } else {
            residentialBranchDraftList.value = residentialBranchDraftList.value.filter(
                (_, i) => i !== index,
            );
        }
        if (!anyResidentialBranchEditing()) return;
        emitResidentialBranchDraft();
    }
};

/** Service onContactJsonAddressCountryUpdate와 동일 — v-model이 country 설정, hydrate로 country 되돌리지 않음 */
function onBranchResidentialCountryUpdate(address: any, newCountry: string | null | undefined) {
    if (!address || typeof address !== 'object') return;
    const c = newCountry != null ? String(newCountry) : '';
    const prevCountry =
        address._countryChangeBaseline !== undefined
            ? String(address._countryChangeBaseline)
            : resolveCountryTotalCode(address.country, countryOptions.value);
    if (c) {
        delete address._showLocationDefaultsOnAdd;
    }
    if (
        applyCountryChangeClearsSubLocation(
            address,
            prevCountry,
            c,
            countryOptions.value,
        )
    ) {
        postcodeOptions.value = [];
        cityOptions.value = [];
        cityOptionsForStateCode.value = '';
    }
    const resolved = resolveCountryTotalCode(c, countryOptions.value);
    address.countryCode = resolved || '';
    address._countryChangeBaseline = resolved;
}

function syncBranchResidentialCountrySearch(address: any) {
    if (!address || typeof address !== 'object') return;
    address._countrySearch = countryCodeToSearchLabel(address.country, countryOptions.value);
}

function onBranchResidentialCountryFocus(address: any) {
    if (address && typeof address === 'object') {
        address._countryChangeBaseline = resolveCountryTotalCode(
            address.country,
            countryOptions.value,
        );
    }
    syncBranchResidentialCountrySearch(address);
}

function onBranchResidentialCountrySelect(address: any, newCountry: string | null | undefined) {
    onBranchResidentialCountryUpdate(address, newCountry);
    if (!address || typeof address !== 'object') return;
    address._countrySearch = newCountry
        ? countryCodeToSearchLabel(newCountry, countryOptions.value)
        : '';
}

async function onBranchResidentialStateSelected(
    address: any,
    newState: string | null | undefined,
) {
    // Register/Corporate branch: jurisdiction MY가 아니어도 useBranchAddresses면 동작
    if (!address) return;
    if (!props.useBranchAddresses && !props.contactListFlatAddressMode) return;
    if (suppressBranchLocationCascade.value) return;

    const st = newState != null ? String(newState) : '';
    if (st && address.country !== MY_MALAYSIA_COUNTRY_CODE) {
        address.country = MY_MALAYSIA_COUNTRY_CODE;
    }
    // State 변경 → City / Postcode 비움 (row editor에서도 비우지만 옵션 로드는 여기서)
    address.city = '';
    address.post_code = '';
    address.postcode = '';
    address.postcodeCode = '';
    postcodeOptions.value = [];
    postcodeOptionsForCityCode.value = '';
    if (st) {
        const stateApi = resolveTotalCodeFromField(st, stateOptions.value) || st;
        await loadCitiesByState(stateApi);
    } else {
        cityOptions.value = [];
        cityOptionsForStateCode.value = '';
        if (postcodeAllOptionsCache.value.length === 0) {
            await loadAllPostcodeOptions();
        }
    }
}

async function onBranchResidentialCitySelected(
    address: any,
    newCity: string | null | undefined,
) {
    if (!address) return;
    if (!props.useBranchAddresses && !props.contactListFlatAddressMode) return;
    if (suppressBranchLocationCascade.value) return;

    const ct = newCity != null ? String(newCity) : '';
    if (ct && address.country !== MY_MALAYSIA_COUNTRY_CODE) {
        address.country = MY_MALAYSIA_COUNTRY_CODE;
    }
    address.post_code = '';
    address.postcode = '';
    address.postcodeCode = '';
    if (ct) {
        const cityApi = resolveTotalCodeFromField(ct, cityOptions.value) || ct;
        await loadPostcodeOptions(cityApi, false);
    } else {
        postcodeOptions.value = [];
        postcodeOptionsForCityCode.value = '';
        if (postcodeAllOptionsCache.value.length === 0) {
            await loadAllPostcodeOptions();
        }
    }
}

function resolveBranchPostcodeNumber(postcodeValue: string): string {
    const raw = String(postcodeValue || '').trim();
    if (!raw) return '';
    const hit = mergedResidentialPostcodeAutocompleteItems.value.find(
        (opt) => opt.TOTAL_CODE === raw || opt.CODE_NAME === raw,
    );
    if (hit?.CODE_NAME) return hit.CODE_NAME;
    // GUID인데 캐시에 없으면 숫자 해석 불가
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) {
        return '';
    }
    return raw;
}

async function handleBranchResidentialPostcodeChange(address: any) {
    if (!address) return;
    if (!props.useBranchAddresses && !props.contactListFlatAddressMode) return;
    const postcodeValue = String(address.post_code || address.postcode || '').trim();
    if (!postcodeValue) return;

    if (address.country !== MY_MALAYSIA_COUNTRY_CODE) {
        address.country = MY_MALAYSIA_COUNTRY_CODE;
    }

    if (postcodeAllOptionsCache.value.length === 0) {
        await loadAllPostcodeOptions();
    }

    const postcodeNumber = resolveBranchPostcodeNumber(postcodeValue);
    if (!postcodeNumber) return;

    const currentStateCode =
        resolveTotalCodeFromField(address.state, stateOptions.value) ||
        (address.state as string) ||
        null;
    const currentCityCode =
        resolveTotalCodeFromField(address.city, cityOptions.value) ||
        (address.city as string) ||
        null;

    const locationInfo = await findStateAndCityByPostcode(
        postcodeNumber,
        currentStateCode,
        currentCityCode,
    );

    if (!locationInfo?.stateCode || !locationInfo.cityCode) return;

    // Postcode 선택 → State / City 자동 채움 (기존 값이 있어도 덮어씀)
    suppressBranchLocationCascade.value = true;
    address._suppressLocationCascade = true;
    isInitializingBranchResidentialLocation.value = true;
    try {
        address.state = locationInfo.stateCode;
        await loadCitiesByState(locationInfo.stateCode);
        address.city = locationInfo.cityCode;
        await loadPostcodeOptions(locationInfo.cityCode, true);
        const keepPc =
            mergedResidentialPostcodeAutocompleteItems.value.find(
                (opt) => opt.TOTAL_CODE === postcodeValue || opt.CODE_NAME === postcodeNumber,
            )?.TOTAL_CODE || postcodeValue;
        address.post_code = keepPc;
        address.postcodeCode = keepPc;
    } finally {
        await nextTick();
        address._suppressLocationCascade = false;
        suppressBranchLocationCascade.value = false;
        isInitializingBranchResidentialLocation.value = false;
    }
}

function queueBranchResidentialState(address: any, v: string | null | undefined) {
    void onBranchResidentialStateSelected(address, v).catch((err) =>
        console.error('[ContactAddressForm] branch residential state selection', err),
    );
}

function queueBranchResidentialCity(address: any, v: string | null | undefined) {
    void onBranchResidentialCitySelected(address, v).catch((err) =>
        console.error('[ContactAddressForm] branch residential city selection', err),
    );
}

function queueBranchResidentialPostcode(address: any) {
    void handleBranchResidentialPostcodeChange(address).catch((err) =>
        console.error('[ContactAddressForm] branch residential postcode selection', err),
    );
}

function sanitizeBranchResidentialRowForEmit(row: any) {
    const copy = { ...(row || {}) };
    clearMyLocationPlaceholdersOnAddress(copy);
    copy.country = normalizeMyLocationFieldForPersist(copy.country, countryOptions.value);
    if (copy.country) {
        copy.countryCode = copy.country;
    }
    if (isMalaysiaBranchAddressRow(copy)) {
        copy.state = normalizeMyLocationFieldForPersist(copy.state, stateOptions.value);
        copy.city = normalizeMyLocationFieldForPersist(copy.city, cityOptions.value);
        const pcTotal = resolveBranchPostcodeTotalCode(copy);
        if (pcTotal) {
            copy.post_code = pcTotal;
            copy.postcodeCode = pcTotal;
        }
    }
    return copy;
}

function emitResidentialBranchDraft() {
    const list = residentialBranchDraftList.value.map((r) => sanitizeBranchResidentialRowForEmit(r));
    emit('update:residentialAddressList', list);
    if (props.useBranchAddresses || props.contactListFlatAddressMode) return;
    const primary = list[0];
    if (!primary || !addressData.value) return;
    const extra: Record<string, unknown> = {};
    const syncVal = primary.IsSync ?? primary.isSync;
    if (syncVal !== undefined) extra.IsSync = syncVal;
    const localAddress = buildMyLocalAddressRecordFromForm({
        addressLine1: primary.street01 || primary.addressLine1 || '',
        addressLine2: primary.street02 || primary.addressLine2 || '',
        addressLine3: primary.street03 || primary.addressLine3 || '',
        country: primary.country || primary.countryCode || '',
        state: primary.state || primary.stateCode || '',
        city: primary.city || primary.cityCode || '',
        postcode:
            primary.postcodeCode ||
            primary.post_code ||
            primary.postcode ||
            primary.postalCode ||
            '',
        countryOpts: countryOptions.value,
        stateOpts: stateOptions.value,
        cityOpts: cityOptions.value,
        postcodeOpts: mergedResidentialPostcodeAutocompleteItems.value,
        extra,
    });
    addressData.value = {
        ...addressData.value,
        localAddress: localAddress as any,
    };
    myAddressData.value = {
        addressLine1: localAddress.addressLine1,
        addressLine2: localAddress.addressLine2,
        addressLine3: localAddress.addressLine3,
        country: localAddress.country,
        state: localAddress.state,
        city: localAddress.city,
        postcode: localAddress.postcode,
    };
    emitModelValueIfChanged(
        mergeAddressIntoParentModel({
            addressType: addressData.value.addressType,
            localAddress: localAddress as AddressInfo,
            foreignAddress: addressData.value.foreignAddress,
        })
    );
}

/** Corporate branch: 편집 중 부모 emit 금지(ContactInfoForm Service/Business와 동일) — Save 시 expose */
watch(
    residentialBranchDraftList,
    () => {
        if (!showBranchResidentialSection.value) return;
        if (isSyncingBranchDraftFromProps.value) return;
        if (props.useBranchAddresses || props.contactListFlatAddressMode) return;
        if (!anyResidentialBranchEditing()) return;
        emitResidentialBranchDraft();
    },
    { deep: true },
);

function buildResidentialBranchListForSave() {
    return residentialBranchDraftList.value.map((r) => sanitizeBranchResidentialRowForEmit(r));
}

/** MY Corporate branch — 표시용 country/state만 (myAddressData·addressData 루프 없음) */
async function loadBranchMyDisplayOptions() {
    if (!props.useBranchAddresses) return;
    await loadCountryOptions();
    await loadStateOptions();
    if (postcodeAllOptionsCache.value.length === 0) {
        await loadAllPostcodeOptions();
    }
    if (Array.isArray(props.residentialAddressList) && props.residentialAddressList.length > 0) {
        if (!anyResidentialBranchEditing()) {
            syncResidentialBranchDraftFromProps(true);
        }
    } else {
        await hydrateAllMalaysiaBranchResidentialRows();
    }
    await hydrateEditingBranchResidentialRows();
}

async function hydrateEditingBranchResidentialRows() {
    if (!props.useBranchAddresses) return;
    const tasks: Promise<void>[] = [];
    Object.entries(residentialBranchEditState.value).forEach(([key, editing]) => {
        if (!editing) return;
        const m = key.match(/^branch-res-(\d+)$/);
        if (!m) return;
        const row = residentialBranchDraftList.value[Number(m[1])];
        if (row) {
            tasks.push(
                hydrateBranchResidentialRowFields(row).catch((err) =>
                    console.error('[ContactAddressForm] hydrate branch residential', err),
                ),
            );
        }
    });
    if (tasks.length > 0) await Promise.all(tasks);
}

onErrorCaptured((err, _instance, info) => {
    console.error('[ContactAddressForm] error', err, info);
    return false;
});

onMounted(async () => {
    window.addEventListener('language-changed', (event: any) => {
        currentLanguage.value = event.detail.language;
    });

    console.log('[ContactAddressForm] mounted', {
        contactCd: props.contactCd,
        useBranchAddresses: props.useBranchAddresses,
        residentialListCount: Array.isArray(props.residentialAddressList)
            ? props.residentialAddressList.length
            : 0,
        draftCount: residentialBranchDraftList.value.length,
        showBranchResidentialSection: showBranchResidentialSection.value,
    });

    try {
        if (isJurisdictionMY.value) {
            await loadMyOptions();
            if (props.useBranchAddresses) {
                await loadBranchMyDisplayOptions();
            }
        } else if (props.useBranchAddresses) {
            await loadBranchMyDisplayOptions();
        }
    } catch (mountErr) {
        console.error('[ContactAddressForm] onMounted failed', mountErr);
    }
});

watch(isJurisdictionMY, async (newValue, oldValue) => {
    if (newValue && !oldValue) {
        await loadMyOptions();
        if (props.useBranchAddresses) {
            await loadBranchMyDisplayOptions();
        }
    }
}, { immediate: false });

watch(
    () => props.useBranchAddresses,
    async (useBranch) => {
        if (!useBranch) return;
        await loadBranchMyDisplayOptions();
    },
);

// MY인 경우 Entity 스타일 주소 데이터 (TDZ 방지를 위해 선행 선언)
const myAddressData = ref({
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    country: '',
    state: '',
    city: '',
    postcode: '',
});
const mergedMyAddress = computed(() =>
    formatMyResidentialSummaryForDisplay(
        myAddressData.value,
        props.modelValue?.localAddress as Record<string, unknown> | undefined,
        {
            countryOpts: countryOptions.value,
            stateOpts: stateOptions.value,
            cityOpts: cityOptions.value,
            postcodeOpts: mergedResidentialPostcodeAutocompleteItems.value,
        }
    )
);

const isResidentialAddressSync = computed(() => {
    const la = props.modelValue?.localAddress as any;
    if (!la) return false;
    const v = la.IsSync ?? la.isSync;
    return v === 1 || v === '1' || v === true;
});

const hasMergedMyAddress = computed(() => mergedMyAddress.value.length > 0);
const showMyAddressReadonlyEmpty = computed(
    () => isJurisdictionMY.value && readonly.value && !hasMergedMyAddress.value
);
const showResidentialAddPlaceholder = computed(
    () =>
        isJurisdictionMY.value &&
        !showBranchResidentialSection.value &&
        !readonly.value &&
        !hasMergedMyAddress.value &&
        !residentialMyExpanded.value
);
const showMyAddressSummary = computed(
    () =>
        isJurisdictionMY.value &&
        !showBranchResidentialSection.value &&
        hasMergedMyAddress.value &&
        !isMyAddressEditing.value
);
const showMyAddressEditor = computed(
    () =>
        isJurisdictionMY.value &&
        !showBranchResidentialSection.value &&
        !showMyAddressSummary.value &&
        !showResidentialAddPlaceholder.value &&
        !showMyAddressReadonlyEmpty.value
);

const showAddressSelectDialog = ref(false);

// MY 주소 Consolidate 다이얼로그
const consolidateDialogOpen = ref(false);
const showConsolidateAddressLink = computed(
    () => isJurisdictionMY.value
        && canEditMyAddress.value
        && !!String(props.contactCd || '').trim(),
);
function onAddressConsolidated() {
    // 병합/삭제 반영 후 블록 재로드
    emit('refresh');
}
const contactCdForSelect = computed(() => String(props.contactCd || '').trim());

async function onAddressCandidateSelected(candidate: ContactAddressCandidate) {
    if (candidate.addressType !== 'M04001') return;
    isInitializingLocation.value = true;
    try {
        await loadCountryOptions();
        await loadStateOptions();
        const mapped = mapCandidateToMyAddressData(candidate.address);
        let countryCode =
            resolveTotalCodeFromField(mapped.country, countryOptions.value) || mapped.country;
        let stateCode =
            resolveTotalCodeFromField(mapped.state, stateOptions.value) || mapped.state;
        if (stateCode) {
            await loadCitiesByState(stateCode);
        }
        let cityCode =
            resolveTotalCodeFromField(mapped.city, cityOptions.value) || mapped.city;
        if (cityCode) {
            await loadPostcodeOptions(String(cityCode).trim(), true).catch(() => {});
        }
        const postcodeCode =
            resolveTotalCodeFromField(mapped.postcode, postcodeOptions.value) || mapped.postcode;
        myAddressData.value = {
            addressLine1: mapped.addressLine1,
            addressLine2: mapped.addressLine2,
            addressLine3: mapped.addressLine3,
            country: countryCode,
            state: stateCode,
            city: cityCode,
            postcode: postcodeCode,
        };
    } finally {
        await nextTick();
        isInitializingLocation.value = false;
    }
}

const toggleMyAddressEdit = () => {
    if (!canEditMyAddress.value) return;
    const next = !isMyAddressEditing.value;
    isMyAddressEditing.value = next;
    if (next) {
        void hydrateResidentialMyAddressFields().catch((err) =>
            console.error('[ContactAddressForm] hydrateResidentialMyAddressFields', err)
        );
    }
};

const openResidentialMyAdd = async () => {
    if (!canEditMyAddress.value) return;
    residentialMyExpanded.value = true;
    residentialShowLocationOnAdd.value = true;
    isMyAddressEditing.value = true;
    await loadMyOptions();
    void loadAllPostcodeOptions().catch(() => {});
};

const cancelResidentialMyDraft = async () => {
    residentialMyExpanded.value = false;
    residentialShowLocationOnAdd.value = false;
    isMyAddressEditing.value = false;
    myAddressData.value = {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        country: '',
        state: '',
        city: '',
        postcode: ''
    };
    await loadMyOptions();
};

watch(isJurisdictionMY, (newValue) => {
    if (!newValue) {
        isMyAddressEditing.value = false;
        residentialMyExpanded.value = false;
    }
});

watch(readonly, (newValue) => {
    if (newValue) {
        isMyAddressEditing.value = false;
        residentialMyExpanded.value = false;
    }
});

watch(hasMergedMyAddress, (hasAddr) => {
    if (hasAddr) {
        residentialMyExpanded.value = false;
    }
});

// MY + 비읽기·주소 없음: '-' placeholder만. 편집 시 + Add 로 블록 오픈.

const shouldShowLocationFields = computed(() =>
    shouldShowMyLocationFieldsWithAddDefault(
        myAddressData.value.country,
        countryOptions.value,
        residentialShowLocationOnAdd.value
    )
);

/** Country autocomplete 검색 문자열 (v-model은 TOTAL_CODE) */
const countrySearch = ref('');

function syncResidentialCountrySearchFromModel() {
    countrySearch.value = countryCodeToSearchLabel(
        myAddressData.value.country,
        countryOptions.value
    );
}

function syncResidentialCountrySnapshotFromMyData() {
    residentialCountrySnapshot.value = resolveCountryTotalCode(
        myAddressData.value.country,
        countryOptions.value
    );
}

function onResidentialCountryFocus() {
    syncResidentialCountrySearchFromModel();
    residentialCountrySnapshot.value = resolveCountryTotalCode(
        myAddressData.value.country,
        countryOptions.value
    );
}

function onMyResidentialCountryUpdate(newCountry: string | null | undefined) {
    const c = newCountry != null ? String(newCountry) : '';
    if (c) residentialShowLocationOnAdd.value = false;
    const prevCountry = residentialCountrySnapshot.value;
    if (
        applyCountryChangeClearsSubLocation(
            myAddressData.value as Record<string, unknown>,
            prevCountry,
            c,
            countryOptions.value
        )
    ) {
        isResettingLocationFromCountryChange.value = true;
        postcodeOptions.value = [];
        cityOptions.value = [];
        cityOptionsForStateCode.value = '';
        nextTick(() => {
            isResettingLocationFromCountryChange.value = false;
        });
    }
    residentialCountrySnapshot.value = resolveCountryTotalCode(c, countryOptions.value);
    countrySearch.value = c
        ? countryCodeToSearchLabel(c, countryOptions.value)
        : '';
}

function myAddressDataMatchesLocalAddress(local: any): boolean {
    if (!local) return false;
    const nv = local as Record<string, unknown>;
    const countryCode =
        resolveTotalCodeFromField(nv.countryCode || local.country, countryOptions.value) ||
        String(local.country || '').trim();
    const stateCode =
        resolveTotalCodeFromField(nv.stateCode || local.state, stateOptions.value) ||
        String(local.state || '').trim();
    const cityCode =
        resolveTotalCodeFromField(nv.cityCode || local.city, cityOptions.value) ||
        String(local.city || '').trim();
    const postcodeCode = String(
        nv.postcodeCode || local.postcode || local.post_code || local.postalCode || ''
    ).trim();
    const md = myAddressData.value;
    return (
        (md.addressLine1 || '') === (local.addressLine1 || '') &&
        (md.addressLine2 || '') === (local.addressLine2 || '') &&
        (md.addressLine3 || '') === (local.addressLine3 || '') &&
        (md.country || '') === (countryCode || '') &&
        (md.state || '') === (stateCode || '') &&
        (md.city || '') === (cityCode || '') &&
        (md.postcode || '') === (postcodeCode || '')
    );
}

function mergeAddressIntoParentModel(partial: AddressModel): AddressModel {
    const base = { ...(props.modelValue ?? {}) } as AddressModel;
    return {
        ...base,
        addressType: partial.addressType ?? base.addressType ?? 'B07001',
        localAddress: partial.localAddress ?? base.localAddress,
        foreignAddress: partial.foreignAddress ?? base.foreignAddress,
    };
}

function emitModelValueIfChanged(payload: AddressModel, force = false) {
    // force=true 는 라디오(addressType) 전환처럼 부모가 즉시 알아야 하는 필드에만 사용.
    // 그 외 필드는 suppressParentEmit=true 이면 부모로 안 올림 (getAddressModelForSave 로 저장 시 일괄 반영).
    if (props.suppressParentEmit && !force) return;
    const merged = mergeAddressIntoParentModel(payload);
    try {
        if (JSON.stringify(merged) === JSON.stringify(props.modelValue ?? {})) return;
    } catch {
        /* emit on compare failure */
    }
    emit('update:modelValue', merged);
}

function buildMyLocalAddressFromMyAddressData(): Record<string, unknown> {
    const newValue = myAddressData.value;
    const currentLocalAddress = (props.modelValue as any)?.localAddress || {};
    const extra: Record<string, unknown> = {};
    if (currentLocalAddress.IsSync !== undefined) extra.IsSync = currentLocalAddress.IsSync;
    if (currentLocalAddress.isSync !== undefined) extra.isSync = currentLocalAddress.isSync;

    return buildMyLocalAddressRecordFromForm({
        addressLine1: newValue.addressLine1,
        addressLine2: newValue.addressLine2,
        addressLine3: newValue.addressLine3,
        country: newValue.country,
        state: newValue.state,
        city: newValue.city,
        postcode: newValue.postcode,
        countryOpts: countryOptions.value,
        stateOpts: stateOptions.value,
        cityOpts: cityOptions.value,
        postcodeOpts: mergedResidentialPostcodeAutocompleteItems.value,
        extra,
    });
}

function getAddressModelForSave(): AddressModel {
    const base = { ...(props.modelValue ?? {}) } as AddressModel;
    const addressType = addressData.value.addressType === 'B07002' ? 'B07002' : 'B07001';
    if (isJurisdictionMY.value && !props.useBranchAddresses && addressType === 'B07001') {
        return mergeAddressIntoParentModel({
            addressType,
            localAddress: buildMyLocalAddressFromMyAddressData() as AddressInfo,
            foreignAddress:
                addressData.value.foreignAddress ||
                base.foreignAddress || {
                    addressLine1: '',
                    addressLine2: '',
                },
        });
    }
    return mergeAddressIntoParentModel({
        addressType,
        localAddress: addressData.value.localAddress ?? base.localAddress,
        foreignAddress: addressData.value.foreignAddress ?? base.foreignAddress,
    });
}

const addressData: WritableComputedRef<AddressModel> = computed<AddressModel>({
    get: () => {
        const value = props.modelValue || { addressType: 'B07001' };
        const normalizedAddressType: AddressType = value.addressType === 'B07002' ? 'B07002' : 'B07001';

        const localAddr = value.localAddress || {
            postalCode: '',
            blockHouseNo: '',
            streetName: '',
            buildingEstateName: '',
            level: '',
            unit: '',
            // MY인 경우 Entity 스타일 주소 필드
            addressLine1: '',
            addressLine2: '',
            addressLine3: '',
            country: '',
            state: '',
            city: '',
            postcode: '',
        };

        // post_code가 있으면 포함
        if (value.localAddress && (value.localAddress as any).post_code) {
            (localAddr as any).post_code = (value.localAddress as any).post_code;
        }

        return {
            addressType: normalizedAddressType,
            localAddress: localAddr,
            foreignAddress: value.foreignAddress || {
                addressLine1: '',
                addressLine2: '',
            },
        };
    },
    set: (value: AddressModel) => {
        const normalizedAddressType: AddressType = value.addressType === 'B07002' ? 'B07002' : 'B07001';

        emitModelValueIfChanged(
            mergeAddressIntoParentModel({
                addressType: normalizedAddressType,
                localAddress: value.localAddress ?? addressData.value.localAddress,
                foreignAddress: value.foreignAddress ?? addressData.value.foreignAddress,
            })
        );
    }
});

// 로딩 상태
const isLoadingCities = ref(false);
const isLoadingPostcodes = ref(false);
const isInitializingLocation = ref(false);
/** Postcode→State/City 역채움 중 State/City watch cascade 금지 */
const suppressLocationCascade = ref(false);
/** Country 변경 직후 localAddress watch가 옛 state/city/postcode로 되돌리지 않도록 */
const isResettingLocationFromCountryChange = ref(false);
const residentialCountrySnapshot = ref('');

// Country 목록 로드 (B15 - Jurisdiction, 세션 캐시)
const loadCountryOptions = async () => {
  if (countryOptions.value.length > 0) return;
  try {
    const { countries } = await ensureCachedMyCountryAndState();
    countryOptions.value = countries;
  } catch (error) {
    console.error('❌ [loadCountryOptions] Country 로드 실패:', error);
    countryOptions.value = [];
  }
};

// State 옵션 로드 (세션 캐시)
const loadStateOptions = async () => {
  if (stateOptions.value.length > 0) return;
  try {
    const { states } = await ensureCachedMyCountryAndState();
    stateOptions.value = states;
  } catch (error) {
    console.error('❌ [loadStateOptions] State 로드 실패:', error);
    stateOptions.value = [];
  }
};

// State별 City 목록 로드 (State GUID/표시명 모두 허용, 동시 호출 레이스 방지)
let citiesLoadSeq = 0;
/** 현재 cityOptions가 어떤 State GUID 기준인지 */
const cityOptionsForStateCode = ref('');
const postcodeOptionsForCityCode = ref('');
const loadCitiesByState = async (stateCode: string | null) => {
  if (!stateCode) {
    cityOptions.value = [];
    cityOptionsForStateCode.value = '';
    return;
  }

  const resolvedState =
    resolveTotalCodeFromField(stateCode, stateOptions.value) || String(stateCode).trim();
  if (!resolvedState || isMyLocationPlaceholderValue(resolvedState)) {
    cityOptions.value = [];
    cityOptionsForStateCode.value = '';
    return;
  }

  const seq = ++citiesLoadSeq;
  isLoadingCities.value = true;
  try {
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('City 목록 로드 타임아웃')), 5000),
    );

    const response = (await Promise.race([
      entityMyApi.getMalaysiaCities(resolvedState, undefined, false),
      timeoutPromise,
    ])) as any;

    if (seq !== citiesLoadSeq) return;

    const cities = response.data?.data || response.data || [];

    cityOptions.value = cities
      .map((city: any) => ({
        TOTAL_CODE: String(city.code || '').trim(),
        CODE_NAME: String(city.name || '').trim(),
      }))
      .filter((item: any) => item.TOTAL_CODE && item.CODE_NAME);
    cityOptionsForStateCode.value = resolvedState.toLowerCase();
  } catch (error) {
    if (seq !== citiesLoadSeq) return;
    console.error('❌ [loadCitiesByState] City 목록 로드 실패:', error);
    cityOptions.value = [];
    cityOptionsForStateCode.value = '';
  } finally {
    if (seq === citiesLoadSeq) {
      isLoadingCities.value = false;
    }
  }
};

/** City 콤보 포커스 시 — 선택 State에 맞는 City 목록이 없으면(또는 다른 State면) 재로드 */
async function ensureCitiesLoadedForState(stateRaw: unknown) {
  const state = String(stateRaw ?? '').trim();
  if (!state || isMyLocationPlaceholderValue(state)) return;
  const resolved =
    resolveTotalCodeFromField(state, stateOptions.value) || state;
  const resolvedKey = resolved.toLowerCase();
  if (
    cityOptions.value.length > 0 &&
    cityOptionsForStateCode.value &&
    cityOptionsForStateCode.value === resolvedKey
  ) {
    return;
  }
  await loadCitiesByState(state);
}

async function ensureCitiesLoadedForCurrentState() {
  await ensureCitiesLoadedForState(myAddressData.value.state);
}

async function ensureCitiesLoadedForBranchRow(address: Record<string, unknown> | null | undefined) {
  if (!address) return;
  await ensureCitiesLoadedForState(address.state);
}

// 모든 Postcode 목록 로드 (세션 캐시, Postcode 콤보·역검색 시에만)
const loadAllPostcodeOptions = async () => {
  if (isLoadingPostcodes.value) {
    return;
  }
  if (postcodeAllOptionsCache.value.length > 0) {
    return;
  }

  isLoadingPostcodes.value = true;
  try {
    postcodeAllOptionsCache.value = await fetchCachedMyAllPostcodeOptions();
  } catch (error) {
    console.error('❌ [loadAllPostcodeOptions] 전체 Postcode 로드 실패:', error);
    postcodeAllOptionsCache.value = [];
  } finally {
    isLoadingPostcodes.value = false;
  }
};

// Postcode 옵션 로드 (City 선택 시) — City GUID/표시명 허용, City별 목록 추적
const loadPostcodeOptions = async (cityCode: string | null, preservePostcodeValue: boolean = false) => {
  if (!cityCode) {
    postcodeOptionsForCityCode.value = '';
    if (postcodeAllOptionsCache.value.length === 0) {
      await loadAllPostcodeOptions();
    }
    if (!preservePostcodeValue && !props.useBranchAddresses) {
      myAddressData.value.postcode = '';
    }
    return;
  }

  const resolvedCity =
    resolveTotalCodeFromField(cityCode, cityOptions.value) || String(cityCode).trim();
  if (!resolvedCity || isMyLocationPlaceholderValue(resolvedCity)) {
    postcodeOptions.value = [];
    postcodeOptionsForCityCode.value = '';
    return;
  }

  isLoadingPostcodes.value = true;
  try {
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Postcode 목록 로드 타임아웃')), 5000),
    );

    const response = (await Promise.race([
      entityMyApi.getMalaysiaPostcodes(resolvedCity),
      timeoutPromise,
    ])) as any;

    const postcodes = response.data?.data || response.data || [];

    // CODE_NAME(우편번호 숫자) 기준으로 중복 제거
    const uniquePostcodesMap = new Map<string, { TOTAL_CODE: string; CODE_NAME: string }>();

    postcodes.forEach((postcode: any) => {
      const codeName = String(postcode.name || '').trim();
      const totalCode = String(postcode.code || postcode.name || '').trim();

      if (codeName && totalCode) {
        if (!uniquePostcodesMap.has(codeName)) {
          uniquePostcodesMap.set(codeName, {
            TOTAL_CODE: totalCode,
            CODE_NAME: codeName,
          });
        }
      }
    });

    postcodeOptions.value = Array.from(uniquePostcodesMap.values());
    postcodeOptionsForCityCode.value = resolvedCity.toLowerCase();
  } catch (error) {
    console.error('❌ [loadPostcodeOptions] Postcode 로드 실패:', error);
    postcodeOptions.value = [];
    postcodeOptionsForCityCode.value = '';
  } finally {
    isLoadingPostcodes.value = false;
  }
};

/** Postcode 콤보 — 선택 City에 맞는 목록이 없으면 재로드 */
async function ensurePostcodesLoadedForCity(cityRaw: unknown) {
  const city = String(cityRaw ?? '').trim();
  if (!city || isMyLocationPlaceholderValue(city)) return;
  const resolved = resolveTotalCodeFromField(city, cityOptions.value) || city;
  const resolvedKey = resolved.toLowerCase();
  if (
    postcodeOptions.value.length > 0 &&
    postcodeOptionsForCityCode.value &&
    postcodeOptionsForCityCode.value === resolvedKey
  ) {
    return;
  }
  await loadPostcodeOptions(city, true);
}

async function onBranchResidentialPostcodeMenu(address: any, open: boolean) {
  if (!open) return;
  await ensureResidentialPostcodeFullCacheForReverse();
  const city = address?.city;
  if (city) {
    await ensurePostcodesLoadedForCity(city);
  }
}

// 우편번호로 State와 City 찾기 (이미 선택된 State/City 정보 활용)
const findStateAndCityByPostcode = async (
  postcodeNumber: string,
  currentStateCode?: string | null,
  currentCityCode?: string | null
) => {
  if (!postcodeNumber || postcodeNumber.trim().length === 0) {
    return null;
  }
  
  try {
    const response = await entityMyApi.getMalaysiaPostcodesByNumber(postcodeNumber.trim());
    const postcodes = (response as any).data?.data || (response as any).data || [];
    
    if (!Array.isArray(postcodes) || postcodes.length === 0) {
      return null;
    }
    
    // 이미 선택된 City가 있으면 해당 City와 일치하는 결과 우선 선택
    if (currentCityCode && postcodes.length > 1) {
      const cityKey = String(currentCityCode).toLowerCase();
      const matchedByCity = postcodes.find(
        (p: any) => String(p.city?.code || '').toLowerCase() === cityKey,
      );
      if (matchedByCity) {
        return {
          stateCode: matchedByCity?.state?.code || null,
          cityCode: matchedByCity?.city?.code || null,
          cityName: matchedByCity?.city?.name || null
        };
      }
    }
    
    // City가 없고 State만 선택되어 있으면 해당 State의 결과 우선 선택
    if (currentStateCode && !currentCityCode && postcodes.length > 1) {
      const stateKey = String(currentStateCode).toLowerCase();
      const matchedByState = postcodes.find(
        (p: any) => String(p.state?.code || '').toLowerCase() === stateKey,
      );
      if (matchedByState) {
        return {
          stateCode: matchedByState?.state?.code || null,
          cityCode: matchedByState?.city?.code || null,
          cityName: matchedByState?.city?.name || null
        };
      }
    }
    
    // 매칭되는 것이 없거나 하나만 있으면 첫 번째 결과 사용
    const firstPostcode = postcodes[0] as any;
    
    return {
      stateCode: firstPostcode?.state?.code || null,
      cityCode: firstPostcode?.city?.code || null,
      cityName: firstPostcode?.city?.name || null
    };
  } catch (error) {
    console.error('❌ [findStateAndCityByPostcode] State/City 찾기 실패:', error);
    return null;
  }
};

// State 선택 시 City 목록 로드
watch(() => myAddressData.value.state, async (newState, oldState) => {
  if (isInitializingLocation.value || suppressLocationCascade.value) {
    return;
  }
  
  // 초기 바인딩: City/Postcode 유지하고 City 옵션만 로드 (No data available 방지)
  if (oldState === undefined && newState) {
    await loadCitiesByState(newState);
    return;
  }
  
  if (newState !== oldState) {
    // State가 입력되면 말레이시아 주소로 간주하여 country를 "B15135"로 자동 바인딩
    if (newState && myAddressData.value.country !== 'B15135') {
      myAddressData.value.country = 'B15135';
    }
    
    myAddressData.value.city = '';
    myAddressData.value.postcode = '';
    postcodeOptions.value = [];
    postcodeOptionsForCityCode.value = '';
    
    if (newState) {
      await loadCitiesByState(newState);
    } else {
      // State가 지워지면 City 옵션 초기화 및 전체 Postcode 목록 다시 로드 (역방향 조회를 위해)
      cityOptions.value = [];
      cityOptionsForStateCode.value = '';
      await loadAllPostcodeOptions();
    }
  }
});

// City 선택 시 Postcode 목록 로드
watch(() => myAddressData.value.city, async (newCity, oldCity) => {
  if (isInitializingLocation.value || suppressLocationCascade.value) {
    return;
  }
  
  // 초기 바인딩: Postcode 값 유지하고 City별 Postcode 옵션만 로드
  if (oldCity === undefined && newCity) {
    await loadPostcodeOptions(newCity, true);
    return;
  }
  
  if (newCity !== oldCity) {
    // City가 입력되면 말레이시아 주소로 간주하여 country를 "B15135"로 자동 바인딩
    if (newCity && myAddressData.value.country !== 'B15135') {
      myAddressData.value.country = 'B15135';
    }
    
    myAddressData.value.postcode = '';
    postcodeOptions.value = [];
    postcodeOptionsForCityCode.value = '';
    
    if (newCity) {
      await loadPostcodeOptions(newCity, false);
    } else {
      if (postcodeAllOptionsCache.value.length === 0) {
        await loadAllPostcodeOptions();
      }
    }
  }
});

// Postcode 선택 시 State와 City 자동 선택 (역방향 조회)
watch(() => myAddressData.value.postcode, async (newPostcode, oldPostcode) => {
  if (isInitializingLocation.value || suppressLocationCascade.value) {
    return;
  }
  
  // Postcode가 입력되었을 때만 역방향 조회 수행 (지워지면 State/City 유지)
  if (newPostcode !== oldPostcode && newPostcode) {
    // Postcode가 입력되면 말레이시아 주소로 간주하여 country를 "B15135"로 자동 바인딩
    if (newPostcode && myAddressData.value.country !== 'B15135') {
      myAddressData.value.country = 'B15135';
    }
    
    // Postcode 옵션이 비어있으면 먼저 로드
    if (
        postcodeAllOptionsCache.value.length === 0 &&
        postcodeOptions.value.length === 0
    ) {
      await loadAllPostcodeOptions();
    }
    
    const postcodeNumber =
        mergedResidentialPostcodeAutocompleteItems.value.find(
            (opt) => opt.TOTAL_CODE === newPostcode || opt.CODE_NAME === newPostcode
        )?.CODE_NAME || (/^[0-9a-f-]{36}$/i.test(String(newPostcode)) ? '' : String(newPostcode));
    if (!postcodeNumber) return;
    
    // 현재 선택된 State/City 정보를 전달하여 더 정확한 매칭
    const locationInfo = await findStateAndCityByPostcode(
      postcodeNumber,
      myAddressData.value.state || null,
      myAddressData.value.city || null
    );
    
    if (locationInfo && locationInfo.stateCode && locationInfo.cityCode) {
      suppressLocationCascade.value = true;
      isInitializingLocation.value = true;
      
      try {
        myAddressData.value.state = locationInfo.stateCode;
        await loadCitiesByState(locationInfo.stateCode);
        myAddressData.value.city = locationInfo.cityCode;
        await loadPostcodeOptions(locationInfo.cityCode, true);
        myAddressData.value.postcode = newPostcode;
      } finally {
        await nextTick();
        suppressLocationCascade.value = false;
        isInitializingLocation.value = false;
      }
    }
  }
});

// MY 주소 데이터 동기화 플래그 (무한 루프 방지)
const isSyncingMyAddress = ref(false);

// CODE를 CODE_NAME으로 변환하는 헬퍼 함수들 (Entity_my와 동일한 패턴)
const findCountryName = (countryCode: string) => {
  if (!countryCode) return '';
  // GUID 형식이 아닌 경우 그대로 반환 (이미 CODE_NAME일 수 있음)
  if (countryCode.length < 30) {
    return countryCode;
  }
  const country = countryOptions.value.find(opt => opt.TOTAL_CODE === countryCode);
  if (country?.CODE_NAME) {
    return country.CODE_NAME;
  }
  // 옵션에서 찾지 못하면 빈 문자열 반환 (GUID 저장 방지)
  console.warn('⚠️ [ContactAddressForm] Country CODE_NAME을 찾을 수 없음:', countryCode);
  return '';
};

const findStateName = (stateCode: string) => {
  if (!stateCode) return '';
  // GUID 형식이 아닌 경우 그대로 반환 (이미 CODE_NAME일 수 있음)
  if (stateCode.length < 30) {
    return stateCode;
  }
  const state = stateOptions.value.find(opt => opt.TOTAL_CODE === stateCode);
  if (state?.CODE_NAME) {
    return state.CODE_NAME;
  }
  // 옵션에서 찾지 못하면 빈 문자열 반환 (GUID 저장 방지)
  console.warn('⚠️ [ContactAddressForm] State CODE_NAME을 찾을 수 없음:', stateCode);
  return '';
};

const findCityName = (cityCode: string) => {
  if (!cityCode) return '';
  // GUID 형식이 아닌 경우 그대로 반환 (이미 CODE_NAME일 수 있음)
  if (cityCode.length < 30) {
    return cityCode;
  }
  const city = cityOptions.value.find(opt => opt.TOTAL_CODE === cityCode);
  if (city?.CODE_NAME) {
    return city.CODE_NAME;
  }
  // 옵션에서 찾지 못하면 빈 문자열 반환 (GUID 저장 방지)
  console.warn('⚠️ [ContactAddressForm] City CODE_NAME을 찾을 수 없음:', cityCode);
  return '';
};

const findPostcodeName = (postcodeCode: string) => {
  if (!postcodeCode) return '';
  // GUID 형식이 아닌 경우 그대로 반환 (이미 CODE_NAME일 수 있음)
  if (postcodeCode.length < 30) {
    return postcodeCode;
  }
  const postcode = mergedResidentialPostcodeAutocompleteItems.value.find(
      (opt) => opt.TOTAL_CODE === postcodeCode
  );
  if (postcode?.CODE_NAME) {
    return postcode.CODE_NAME;
  }
  // 옵션에서 찾지 못하면 빈 문자열 반환 (GUID 저장 방지)
  console.warn('⚠️ [ContactAddressForm] Postcode CODE_NAME을 찾을 수 없음:', postcodeCode);
  return '';
};

// MY 주소 데이터 변경 시 addressData 업데이트 (country/state/city는 TOTAL_CODE)
watch(() => myAddressData.value, (newValue) => {
  if (props.useBranchAddresses) return;
  if (isSyncingMyAddress.value) return;
  if (isInitializingLocation.value) return;
  if (isJurisdictionMY.value && addressData.value.addressType === 'B07001') {
    isSyncingMyAddress.value = true;
    try {
      const updatedAddressData = mergeAddressIntoParentModel({
        addressType: addressData.value.addressType,
        localAddress: buildMyLocalAddressFromMyAddressData() as AddressInfo,
        foreignAddress:
            addressData.value.foreignAddress ||
            (props.modelValue as any)?.foreignAddress || {
                addressLine1: '',
                addressLine2: '',
            },
      });
      emitModelValueIfChanged(updatedAddressData);
    } finally {
      nextTick(() => {
        isSyncingMyAddress.value = false;
      });
    }
  }
}, { deep: true });

// addressData 변경 시 MY 주소 데이터 동기화
watch(() => addressData.value.localAddress, async (newValue) => {
  if (props.useBranchAddresses) return;
  if (isSyncingMyAddress.value) return;
  if (isInitializingLocation.value) return;
  if (isResettingLocationFromCountryChange.value) return;
  if (isJurisdictionMY.value && newValue) {
    isSyncingMyAddress.value = true;
    try {
      // 초기 바인딩 중 watch들이 postcode를 지우지 않도록 보호
      isInitializingLocation.value = true;

      // readonly: 분기 유지 — 옵션 로드 후 countryCode 우선 바인딩(비-readonly와 동일), 누락 코드는 옵션에 보강
      if (readonly.value) {
        console.log('🔍 [ContactAddressForm] watch localAddress readonly - newValue:', newValue);
        await loadCountryOptions();
        await loadStateOptions();
        const nv = newValue as any;
        let countryCode = resolveTotalCodeFromField(
            nv.countryCode || newValue.country,
            countryOptions.value
        );
        let stateCode = resolveTotalCodeFromField(
            nv.stateCode || newValue.state,
            stateOptions.value
        );
        let cityCode = resolveTotalCodeFromField(nv.cityCode || newValue.city, cityOptions.value);
        const postcodeCode =
            nv.postcodeCode || newValue.post_code || newValue.postcode || newValue.postalCode || '';
        if (stateCode) {
          await loadCitiesByState(stateCode);
        }
        if (cityCode && String(cityCode).trim()) {
          await loadPostcodeOptions(String(cityCode).trim(), true).catch(() => {});
        }
        if (countryCode) {
          const countryExists = countryOptions.value.some((opt) => opt.TOTAL_CODE === countryCode);
          if (!countryExists) {
            countryOptions.value.push({
              TOTAL_CODE: String(countryCode).trim(),
              CODE_NAME: String(newValue.country || countryCode).trim().toUpperCase(),
            });
          }
        }
        if (postcodeCode && String(postcodeCode).trim()) {
          const postcodeExists = postcodeOptions.value.some((opt) => opt.TOTAL_CODE === postcodeCode);
          if (!postcodeExists) {
            postcodeOptions.value.push({
              TOTAL_CODE: String(postcodeCode).trim(),
              CODE_NAME: String(newValue.postcode || newValue.post_code || postcodeCode).trim(),
            });
          }
        }
        myAddressData.value = {
          addressLine1: newValue.addressLine1 || '',
          addressLine2: newValue.addressLine2 || '',
          addressLine3: newValue.addressLine3 || '',
          country: countryCode,
          state: stateCode,
          city: cityCode,
          postcode: postcodeCode,
        };
        syncResidentialCountrySnapshotFromMyData();
        console.log('✅ [ContactAddressForm] MY address data updated from localAddress (readonly):', {
          myAddressData: myAddressData.value,
          newValue,
          postcode: myAddressData.value.postcode,
          post_code_from_newValue: newValue.post_code
        });
      } else {
        if (myAddressDataMatchesLocalAddress(newValue)) {
          syncResidentialCountrySearchFromModel();
          syncResidentialCountrySnapshotFromMyData();
          return;
        }
        if (countryOptions.value.length === 0) {
          await loadCountryOptions();
        }
        if (stateOptions.value.length === 0) {
          await loadStateOptions();
        }
        let countryCode = resolveTotalCodeFromField(
            newValue.countryCode || newValue.country,
            countryOptions.value
        );
        let stateCode = resolveTotalCodeFromField(
            newValue.stateCode || newValue.state,
            stateOptions.value
        );
        let cityCode = resolveTotalCodeFromField(
            newValue.cityCode || newValue.city,
            cityOptions.value
        );
        const postcodeCode = newValue.postcodeCode || newValue.postcode || newValue.post_code || '';

        if (countryCode) {
          const countryExists = countryOptions.value.some((opt) => opt.TOTAL_CODE === countryCode);
          if (!countryExists) {
            const nameMatch = countryOptions.value.find(
                (opt) =>
                    opt.CODE_NAME === String(newValue.country || '').trim() ||
                    opt.TOTAL_CODE === String(newValue.country || '').trim()
            );
            if (nameMatch) {
              countryCode = nameMatch.TOTAL_CODE;
            } else {
              countryOptions.value.push({
                TOTAL_CODE: String(countryCode).trim(),
                CODE_NAME: String(newValue.country || countryCode).trim().toUpperCase(),
              });
            }
          }
        }
        
        // State가 있으면 City 목록 로드
        if (stateCode) {
          await loadCitiesByState(stateCode);
        }
        
        // City가 있으면 City 옵션에 추가 및 Postcode 목록 로드
        if (cityCode && cityCode.trim()) {
          const cityExists = cityOptions.value.some(opt => opt.TOTAL_CODE === cityCode);
          if (!cityExists) {
            cityOptions.value.push({
              TOTAL_CODE: String(cityCode).trim(),
              CODE_NAME: String(cityCode).trim()
            });
            // City 이름 조회 시도
            entityMyApi.getMalaysiaCities(undefined, cityCode)
              .then((cityResponse: any) => {
                const cityData = cityResponse.data?.data || cityResponse.data;
                const cityInfo = Array.isArray(cityData) ? cityData[0] : (cityData || {});
                if (cityInfo?.name) {
                  const cityIndex = cityOptions.value.findIndex(opt => opt.TOTAL_CODE === cityCode);
                  if (cityIndex >= 0) {
                    cityOptions.value[cityIndex].CODE_NAME = String(cityInfo.name).trim();
                  }
                }
              })
              .catch(() => {});
          }
          // City가 있으면 Postcode 목록 로드
          await loadPostcodeOptions(cityCode, true).catch(() => {});
        }
        
        // Postcode가 있으면 postcodeOptions에 추가
        if (postcodeCode && postcodeCode.trim()) {
          const postcodeExists = postcodeOptions.value.some(opt => opt.TOTAL_CODE === postcodeCode);
          if (!postcodeExists) {
            postcodeOptions.value.push({
              TOTAL_CODE: String(postcodeCode).trim(),
              CODE_NAME: String(postcodeCode).trim()
            });
            console.log('✅ [ContactAddressForm] Postcode 추가됨:', postcodeCode);
          }
        }
        
        // countryCode, stateCode, cityCode, postcodeCode를 우선 사용하여 바인딩
        myAddressData.value = {
          addressLine1: newValue.addressLine1 || '',
          addressLine2: newValue.addressLine2 || '',
          addressLine3: newValue.addressLine3 || '',
          country: countryCode,
          state: stateCode,
          city: cityCode,
          postcode: postcodeCode,
        };
        syncResidentialCountrySearchFromModel();
        syncResidentialCountrySnapshotFromMyData();
        console.log('✅ [ContactAddressForm] MY address data updated from localAddress:', {
          myAddressData: myAddressData.value,
          newValue,
          postcodeOptionsLength: postcodeOptions.value.length
        });
      }
    } finally {
      nextTick(() => {
        setTimeout(() => {
          isInitializingLocation.value = false;
        }, 200);
        isSyncingMyAddress.value = false;
      });
    }
  }
}, { deep: true });

const isPostalCodeSearched = ref(false);
const isSearching = ref(false);
const postalCodeError = ref<string | null>(null);

// Postal Code 검색
const searchPostalCode = async () => {
    if (!addressData.value.localAddress?.postalCode || addressData.value.localAddress.postalCode.trim().length === 0) {
        return;
    }
    
    isSearching.value = true;
    postalCodeError.value = null; // 에러 초기화
    
    try {
        const response = await contactsApi.searchPostalCode(addressData.value.localAddress.postalCode);
        
        // 응답 구조: response.data.data 또는 response.data
        const result = (response as any).data?.data || (response as any).data;
        
        if (result) {
            // 빈 문자열이어도 그대로 설정 (빈칸으로 채움) - 명시적으로 설정하여 기존 값 덮어쓰기
            // result에 값이 있으면 그대로 사용하고, 없으면 빈 문자열로 명시적으로 설정
            const streetName = result.hasOwnProperty('streetName') ? (result.streetName || '') : '';
            const buildingName = result.hasOwnProperty('buildingEstateName') ? (result.buildingEstateName || '') : '';
            const blockNo = result.hasOwnProperty('blockHouseNo') ? (result.blockHouseNo || '') : '';
            
            // 기존 props.modelValue의 모든 필드를 유지하면서 address 부분만 업데이트
            // 이렇게 하면 firstName, surname, contactInfo 등 다른 필드들이 초기화되지 않음
            const updatedAddressData = {
                ...props.modelValue,  // 기존의 모든 필드 유지 (firstName, surname, contactInfo 등)
                localAddress: {
                    ...addressData.value.localAddress,
                    postalCode: addressData.value.localAddress?.postalCode || '', // postalCode 유지
                    streetName,
                    buildingEstateName: buildingName,
                    blockHouseNo: blockNo,
                    level: addressData.value.localAddress?.level || '', // level 유지
                    unit: addressData.value.localAddress?.unit || ''    // unit 유지
                }
            };
            
            // force=true — Create 페이지들이 suppressParentEmit=true 로 넘겨도 우편번호 검색 결과는 부모까지 전파해야 함
            emitModelValueIfChanged(updatedAddressData, true);
            isPostalCodeSearched.value = true;
            postalCodeError.value = null; // 성공 시 에러 초기화
        }
    } catch (error: any) {
        // 백엔드에서 반환한 다국어 에러 메시지 추출
        const responseData = error?.response?.data;
        
        // 에러 메시지 추출
        // error가 객체인 경우: responseData.error.message
        // error가 문자열인 경우: responseData.error (문자열 자체)
        let errorMessage = '';
        
        if (responseData?.error) {
            if (typeof responseData.error === 'object' && responseData.error.message) {
                // error가 객체인 경우
                errorMessage = responseData.error.message;
            } else if (typeof responseData.error === 'string') {
                // error가 문자열인 경우 (예: "No inquiry results.")
                // 백엔드에서 다국어 메시지를 반환해야 하는데 문자열로 오는 경우
                // 에러 코드를 확인해서 다국어 메시지로 변환
                const errorCode = responseData.error?.code || responseData.errorCode;
                
                // 백엔드에서 이미 다국어 메시지로 변환되어 오므로 그대로 사용
                errorMessage = responseData.error;
            } else {
                errorMessage = responseData.error;
            }
        }
        
        // 백엔드에서 반환한 다국어 메시지만 사용 (fallback 제거)
        if (!errorMessage) {
            errorMessage = responseData?.message || error?.message || '';
        }
        
        postalCodeError.value = errorMessage;
        console.error('Postal code search failed:', error);
    } finally {
        isSearching.value = false;
    }
};

const updateAddressType = (value: string | null | undefined) => {
    const normalized: AddressType = value === 'B07002' ? 'B07002' : 'B07001';

    // force=true — Create 페이지들이 suppressParentEmit=true 로 넘겨도 라디오 전환은 부모까지 전파해야 함
    // (아니면 addressData 가 props.modelValue 파생 computed 라 라디오 UI 자체가 반응 못 함)
    emitModelValueIfChanged(
        mergeAddressIntoParentModel({
            addressType: normalized,
            localAddress: addressData.value.localAddress,
            foreignAddress: addressData.value.foreignAddress,
        }),
        true
    );
};

defineExpose({
    getAddressModelForSave,
    getResidentialBranchListForSave: () =>
        useMultiResidentialListUi.value ? buildResidentialBranchListForSave() : null,
});

watch(() => addressData.value.addressType, () => {
    isPostalCodeSearched.value = false;
    postalCodeError.value = null;
});

watch(() => addressData.value.localAddress?.postalCode, () => {
    isPostalCodeSearched.value = false;
    postalCodeError.value = null;
});
</script>

<template>
    <v-card class="contact-address-section">
        <v-card-title
            class="contact-section-title d-flex align-center ga-2 flex-wrap"
            :class="{ 'contact-section-title--collapsible': useContactListAddressSectionCollapse }"
            @click="toggleAddressSectionExpanded"
        >
            <v-icon
                v-if="useContactListAddressSectionCollapse"
                size="20"
                class="flex-shrink-0"
            >
                {{ addressSectionExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
            </v-icon>
            <span>{{ residentialAddressLabel }}</span>
        </v-card-title>
        <v-expand-transition>
            <v-card-text v-show="!useContactListAddressSectionCollapse || addressSectionExpanded">
            <!-- MY가 아닌 경우에만 라디오 버튼 표시 -->
            <v-radio-group
                v-if="!isJurisdictionMY"
                :model-value="addressData.addressType"
                :readonly="readonly"
                class="mb-4"
                inline
                @update:model-value="updateAddressType"
            >
                <v-radio 
                    :label="props.isIndividual ? residentialAddressLabel : (readonly ? residentialAddressLabel : (t('contact.Local Address') || 'Local Address'))" 
                    value="B07001" 
                />
                <v-radio 
                    :label="props.isIndividual ? (t('contact.Service Address') || 'Service Address') : (readonly ? (t('contact.Service Address') || 'Service Address') : (t('contact.Foreign Address') || 'Foreign Address'))" 
                    value="B07002" 
                />
            </v-radio-group>

            <!-- Postal Code 검색 에러 메시지 -->
            <v-alert
                v-if="addressData.addressType === 'B07001' && postalCodeError"
                type="error"
                density="compact"
                class="mb-4"
                closable
                @click:close="postalCodeError = null"
            >
                {{ postalCodeError }}
            </v-alert>

            <!-- MY인 경우 항상 Local Address 필드 표시 -->
            <template v-if="showMyCorporateBranchAddressUi || addressData.addressType === 'B07001'">
                <!-- MY / MY Corporate branch 주소 -->
                <template v-if="showMyCorporateBranchAddressUi">
                    <div v-if="showBranchResidentialSection" class="mb-4">
                        <div class="d-flex justify-end align-center ga-1 flex-wrap mb-3">
                            <v-spacer />
                            <v-btn
                                v-show="canEditMyAddress"
                                size="small"
                                variant="text"
                                color="primary"
                                @click="addBranchResidentialRow"
                            >
                                <v-icon start size="16">mdi-plus</v-icon>
                                {{ t('contact.Add') || 'Add' }}
                            </v-btn>
                            <v-btn
                                v-show="showConsolidateAddressLink"
                                size="small"
                                variant="text"
                                color="primary"
                                @click="consolidateDialogOpen = true"
                            >
                                <v-icon start size="16">mdi-merge</v-icon>
                                {{ t('contact.Consolidate') || 'Consolidate' }}
                            </v-btn>
                        </div>
                        <div
                            v-for="(address, index) in residentialBranchDraftList"
                            :key="`branch-residential-${index}`"
                            class="mb-3"
                        >
                            <ContactResidentialBranchRowEditor
                                :address="address"
                                :index="index"
                                :is-editing="isResidentialBranchEditing(index)"
                                :can-edit="canEditMyAddress"
                                :can-delete="canDeleteBranchResidentialRow(address)"
                                :register-address-card-style="!!props.registerAddressDeleteContext"
                                :show-show-address-button="props.useBranchAddresses && !props.hideAddressConsolidationUi && isResidentialBranchEditing(index)"
                                :show-multi-hint="index === 0 && showResidentialMultiHintEffective"
                                :multi-hint-text="props.residentialMultiHintText"
                                :show-location-fields="!!branchResidentialRowShowLocationFields[index]"
                                :delete-loading="isDeleting(`residential-${index}`)"
                                :country-options="countryOptions"
                                :state-options="stateOptions"
                                :city-options="cityOptions"
                                :postcode-items="getBranchResidentialPostcodeComboItems(address)"
                                :is-loading-cities="isLoadingCities"
                                :is-loading-postcodes="isLoadingPostcodes"
                                :labels="branchRowEditorLabels"
                                @toggle-edit="toggleResidentialBranchEdit(index)"
                                @remove="removeBranchResidentialRow(index)"
                                @open-show-address="openBranchResidentialShowAddress(index)"
                                @country-focus="onBranchResidentialCountryFocus(address)"
                                @country-select="onBranchResidentialCountrySelect(address, $event)"
                                @state-change="queueBranchResidentialState(address, $event)"
                                @city-change="queueBranchResidentialCity(address, $event)"
                                @city-focus="ensureCitiesLoadedForBranchRow(address)"
                                @postcode-menu="onBranchResidentialPostcodeMenu(address, $event)"
                                @postcode-change="() => queueBranchResidentialPostcode(address)"
                            />
                        </div>
                        <v-alert
                            v-if="showBranchResidentialEmptyPlaceholder"
                            type="info"
                            variant="tonal"
                            density="comfortable"
                            class="mb-4"
                        >
                            -
                        </v-alert>
                    </div>

                    <template v-if="!showBranchResidentialSection">
                    <div class="d-flex align-center ga-1 flex-wrap mb-3">
                        <div v-if="!suppressParentEmit" class="text-subtitle-2">{{ residentialAddressLabel }}</div>
                        <v-spacer />
                        <v-btn
                            v-show="showResidentialAddPlaceholder && canEditMyAddress"
                            size="small"
                            variant="text"
                            color="primary"
                            @click="openResidentialMyAdd"
                        >
                            <v-icon start size="16">mdi-plus</v-icon>
                            {{ t('contact.Add') || 'Add' }}
                        </v-btn>
                        <v-btn
                            v-if="contactCdForSelect && showMyAddressEditor && !props.hideAddressConsolidationUi"
                            size="small"
                            variant="outlined"
                            color="primary"
                            @click="showAddressSelectDialog = true"
                        >
                            <v-icon start size="16">mdi-map-marker-check</v-icon>
                            {{ t('contact.Select Address') || 'Select Address' }}
                        </v-btn>
                        <v-btn
                            v-if="canEditMyAddress && hasMergedMyAddress"
                            size="small"
                            variant="text"
                            color="primary"
                            @click="toggleMyAddressEdit"
                        >
                            <v-icon start size="16">{{ showMyAddressSummary ? 'mdi-pencil' : 'mdi-close' }}</v-icon>
                            {{ showMyAddressSummary ? ((t('contact.Edit') || 'Edit')) : ((t('contact.Cancel') || 'Cancel')) }}
                        </v-btn>
                    </div>

                    <v-alert
                        v-show="showMyAddressSummary"
                        type="info"
                        variant="tonal"
                        density="comfortable"
                        class="mb-4 my-address-summary"
                    >
                        <div class="d-flex justify-space-between align-center flex-wrap gap-2">
                            <div class="d-flex align-center flex-wrap flex-grow-1">
                                <span class="flex-grow-1 text-wrap">{{ mergedMyAddress || '-' }}</span>
                                <v-chip
                                    v-if="isResidentialAddressSync"
                                    color="primary"
                                    variant="flat"
                                    size="small"
                                    class="ml-2 flex-shrink-0"
                                >
                                    {{ t('contact.Sync') || 'Sync' }}
                                </v-chip>
                            </div>
                            <div
                                v-if="showResidentialMultiHintEffective"
                                class="d-flex align-center ga-2 flex-wrap"
                            >
                                <div class="d-flex align-center ga-1 flex-wrap text-right">
                                    <v-icon size="18" color="warning" icon="mdi-alert-circle-outline" />
                                    <span class="text-caption text-medium-emphasis">{{ props.residentialMultiHintText }}</span>
                                </div>
                                <v-btn
                                    v-if="props.onReSyncAddressClick"
                                    size="small"
                                    variant="tonal"
                                    color="primary"
                                    rounded="pill"
                                    prepend-icon="mdi-sync"
                                    class="resync-action-btn"
                                    @click="props.onReSyncAddressClick()"
                                >
                                    Address Consolidation
                                </v-btn>
                            </div>
                        </div>
                    </v-alert>

                    <v-alert
                        v-show="showResidentialAddPlaceholder || showMyAddressReadonlyEmpty"
                        type="info"
                        variant="tonal"
                        density="comfortable"
                        class="mb-4"
                    >
                        <div class="d-flex justify-end align-center flex-wrap gap-2">
                            <div class="d-flex align-center ga-1 flex-wrap text-right">
                                <v-icon
                                    v-if="showResidentialMultiHintEffective"
                                    size="18"
                                    color="warning"
                                    icon="mdi-alert-circle-outline"
                                />
                                <span class="text-caption text-medium-emphasis" v-if="showResidentialMultiHintEffective">
                                    {{ props.residentialMultiHintText }}
                                </span>
                                <span v-else>-</span>
                            </div>
                            <v-btn
                                v-if="showResidentialMultiHintEffective && props.onReSyncAddressClick"
                                size="small"
                                variant="tonal"
                                color="primary"
                                rounded="pill"
                                prepend-icon="mdi-sync"
                                class="resync-action-btn"
                                @click="props.onReSyncAddressClick()"
                            >
                                Address Consolidation
                            </v-btn>
                        </div>
                    </v-alert>

                    <div v-show="showMyAddressEditor">
                        <div
                            v-if="!hasMergedMyAddress && residentialMyExpanded"
                            class="d-flex justify-end mb-2"
                        >
                            <v-btn
                                size="small"
                                variant="text"
                                color="primary"
                                @click="cancelResidentialMyDraft"
                            >
                                <v-icon start size="16">mdi-close</v-icon>
                                {{ t('contact.Cancel') || 'Cancel' }}
                            </v-btn>
                        </div>
                        <!-- Address Line 1 -->
                        <v-row dense>
                            <v-col cols="12">
                                <v-text-field 
                                    v-model="myAddressData.addressLine1" 
                                    :label="t('contact.Address Line 1') || 'Address Line 1'" 
                                    :readonly="readonly"
                                    :class="{ 'readonly-field': readonly }"
                                    variant="outlined" 
                                    density="comfortable" 
                                    hide-details 
                                />
                            </v-col>
                        </v-row>
                        
                        <!-- Address Line 2 -->
                        <v-row dense>
                            <v-col cols="12">
                                <v-text-field 
                                    v-model="myAddressData.addressLine2" 
                                    :label="t('contact.Address Line 2') || 'Address Line 2'" 
                                    :readonly="readonly"
                                    :class="{ 'readonly-field': readonly }"
                                    variant="outlined" 
                                    density="comfortable" 
                                    hide-details 
                                />
                            </v-col>
                        </v-row>
                        
                        <!-- Address Line 3 -->
                        <v-row dense>
                            <v-col cols="12">
                                <v-text-field 
                                    v-model="myAddressData.addressLine3" 
                                    :label="t('contact.Address Line 3') || 'Address Line 3'" 
                                    :readonly="readonly"
                                    :class="{ 'readonly-field': readonly }"
                                    variant="outlined" 
                                    density="comfortable" 
                                    hide-details 
                                />
                            </v-col>
                        </v-row>
                        
                        <!-- Country -->
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <!-- Readonly일 때는 텍스트 필드로 표시 -->
                                <v-text-field
                                    v-if="readonly"
                                    :model-value="getCountryDisplayName"
                                    :label="t('contact.Country') || 'Country'"
                                    :readonly="true"
                                    :class="{ 'readonly-field': true }"
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                />
                                <v-autocomplete
                                    v-else
                                    v-model="myAddressData.country"
                                    v-model:search="countrySearch"
                                    :items="countryOptions"
                                    :label="t('contact.Country') || 'Country'"
                                    item-title="CODE_NAME"
                                    item-value="TOTAL_CODE"
                                    variant="outlined"
                                    density="comfortable"
                                    :placeholder="t('contact.Please Select') || 'Please Select'"
                                    hide-details
                                    clearable
                                    :auto-select-first="false"
                                    @focus="onResidentialCountryFocus"
                                    @update:model-value="onMyResidentialCountryUpdate"
                                />
                            </v-col>
                            
                            <!-- State -->
                            <v-col v-if="shouldShowLocationFields" cols="12" md="6">
                                <!-- Readonly일 때는 텍스트 필드로 표시 -->
                                <v-text-field
                                    v-if="readonly"
                                    :model-value="getStateDisplayName"
                                    :label="t('contact.State') || 'State'"
                                    :readonly="true"
                                    :class="{ 'readonly-field': true }"
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                />
                                <v-autocomplete
                                    v-else
                                    v-model="myAddressData.state"
                                    :items="stateOptions"
                                    :label="t('contact.State') || 'State'"
                                    item-title="CODE_NAME"
                                    item-value="TOTAL_CODE"
                                    variant="outlined"
                                    density="comfortable"
                                    :placeholder="t('contact.Please Select') || 'Please Select'"
                                    hide-details
                                    clearable
                                />
                            </v-col>
                        </v-row>
                        
                        <!-- City, Postcode -->
                        <v-row v-if="shouldShowLocationFields" dense>
                            <v-col cols="12" md="6">
                                <!-- Readonly일 때는 텍스트 필드로 표시 -->
                                <v-text-field
                                    v-if="readonly"
                                    :model-value="getCityDisplayName"
                                    :label="t('contact.City') || 'City'"
                                    :readonly="true"
                                    :class="{ 'readonly-field': true }"
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                />
                                <v-autocomplete
                                    v-else
                                    v-model="myAddressData.city"
                                    :items="cityOptions"
                                    :label="t('contact.City') || 'City'"
                                    item-title="CODE_NAME"
                                    item-value="TOTAL_CODE"
                                    variant="outlined"
                                    density="comfortable"
                                    :placeholder="t('contact.Please Select') || 'Please Select'"
                                    hide-details
                                    clearable
                                    :loading="isLoadingCities"
                                    @focus="ensureCitiesLoadedForCurrentState"
                                    @click:control="ensureCitiesLoadedForCurrentState"
                                />
                            </v-col>
                            
                            <v-col cols="12" md="6">
                                <!-- Readonly일 때는 텍스트 필드로 표시 -->
                                <v-text-field
                                    v-if="readonly"
                                    :model-value="getPostcodeDisplayName"
                                    :label="t('contact.Postcode') || 'Postcode'"
                                    :readonly="true"
                                    :class="{ 'readonly-field': true }"
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                />
                                <v-autocomplete
                                    v-else
                                    v-model="myAddressData.postcode"
                                    :items="getResidentialPostcodeComboItems()"
                                    :label="t('contact.Postcode') || 'Postcode'"
                                    item-title="CODE_NAME"
                                    item-value="TOTAL_CODE"
                                    variant="outlined"
                                    density="comfortable"
                                    :placeholder="t('contact.Please Select') || 'Please Select'"
                                    hide-details
                                    clearable
                                    :loading="isLoadingPostcodes"
                                    @update:menu="
                                        $event &&
                                        void (async () => {
                                            await ensureResidentialPostcodeFullCacheForReverse();
                                            if (myAddressData.city) {
                                                await ensurePostcodesLoadedForCity(myAddressData.city);
                                            }
                                        })()
                                    "
                                />
                            </v-col>
                        </v-row>
                    </div>
                    </template>
                </template>
                
                <!-- MY가 아닌 경우 기존 필드 -->
                <template v-else>
                    <!-- 첫 번째 줄: 우편번호만 -->
                    <v-row dense>
                        <v-col cols="12" md="4">
                            <!-- Readonly일 때는 일반 텍스트 필드로 표시 -->
                            <v-text-field
                                v-if="isFieldReadonly('localAddress.postalCode')"
                                :model-value="addressData.localAddress!.postalCode"
                                :label="t('contact.Postal Code') || 'Postal Code'"
                                :readonly="true"
                                :class="{ 'readonly-field': true }"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            >
                                <template #append-inner>
                                    <SyncFieldIndicator :change="syncChange('postalCode')" />
                                </template>
                            </v-text-field>
                            <!-- 수정 가능할 때는 검색 아이콘 포함 -->
                            <div v-else class="d-flex align-center ga-1">
                                <v-text-field
                                    v-model="addressData.localAddress!.postalCode"
                                    :label="t('contact.Postal Code') || 'Postal Code'"
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                    :append-inner-icon="hidePostalSearch ? undefined : 'mdi-magnify'"
                                    :loading="isSearching"
                                    class="flex-grow-1"
                                    @click:append-inner="hidePostalSearch ? undefined : searchPostalCode()"
                                />
                                <SyncFieldIndicator :change="syncChange('postalCode')" />
                            </div>
                        </v-col>
                    </v-row>
                    
                    <!-- 두 번째 줄: Block/House No, Street Name, Building/Estate Name -->
                    <v-row dense>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="addressData.localAddress!.blockHouseNo"
                                :label="t('contact.Block/House No') || 'Block/House No'"
                                :disabled="isFieldReadonly('localAddress.blockHouseNo') || isPostalCodeSearched"
                                :readonly="isFieldReadonly('localAddress.blockHouseNo') || isPostalCodeSearched || isJurisdictionSG"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                :class="(isFieldReadonly('localAddress.blockHouseNo') || isPostalCodeSearched || isJurisdictionSG) ? 'postal-readonly-field' : ''"
                            >
                                <template #append-inner>
                                    <SyncFieldIndicator :change="syncChange('blockHouseNo', 'blockNo', 'blkhseNo')" />
                                </template>
                            </v-text-field>
                        </v-col>
                        
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="addressData.localAddress!.streetName"
                                :label="t('contact.Street Name') || 'Street Name'"
                                :disabled="isFieldReadonly('localAddress.streetName') || isPostalCodeSearched"
                                :readonly="isFieldReadonly('localAddress.streetName') || isPostalCodeSearched || isJurisdictionSG"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                :class="(isFieldReadonly('localAddress.streetName') || isPostalCodeSearched || isJurisdictionSG) ? 'postal-readonly-field' : ''"
                            >
                                <template #append-inner>
                                    <SyncFieldIndicator :change="syncChange('streetName')" />
                                </template>
                            </v-text-field>
                        </v-col>
                        
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="addressData.localAddress!.buildingEstateName"
                                :label="t('contact.Building/Estate Name') || 'Building/Estate Name'"
                                :disabled="isFieldReadonly('localAddress.buildingEstateName') || isPostalCodeSearched"
                                :readonly="isFieldReadonly('localAddress.buildingEstateName') || isPostalCodeSearched || isJurisdictionSG"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                :class="(isFieldReadonly('localAddress.buildingEstateName') || isPostalCodeSearched || isJurisdictionSG) ? 'postal-readonly-field' : ''"
                            >
                                <template #append-inner>
                                    <SyncFieldIndicator :change="syncChange('buildingEstateName', 'buildingName')" />
                                </template>
                            </v-text-field>
                        </v-col>
                    </v-row>
                    
                    <!-- 세 번째 줄: Level, Unit -->
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="addressData.localAddress!.level"
                                :label="t('contact.Level') || 'Level'"
                                :readonly="isFieldReadonly('localAddress.level')"
                                :class="{ 'readonly-field': isFieldReadonly('localAddress.level') }"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            >
                                <template #append-inner>
                                    <SyncFieldIndicator :change="syncChange('level', 'levelNo')" />
                                </template>
                            </v-text-field>
                        </v-col>
                        
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="addressData.localAddress!.unit"
                                :label="t('contact.Unit') || 'Unit'"
                                :readonly="isFieldReadonly('localAddress.unit')"
                                :class="{ 'readonly-field': isFieldReadonly('localAddress.unit') }"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            >
                                <template #append-inner>
                                    <SyncFieldIndicator :change="syncChange('unit', 'unitNo')" />
                                </template>
                            </v-text-field>
                        </v-col>
                    </v-row>
                </template>
            </template>

            <template v-else>
                <v-row dense>
                    <v-col cols="12">
                        <v-text-field
                            v-model="addressData.foreignAddress!.addressLine1"
                            :label="t('contact.Foreign Address Line 1') || 'Foreign Address Line 1'"
                            :readonly="isFieldReadonly('foreignAddress.addressLine1')"
                            :class="{ 'readonly-field': isFieldReadonly('foreignAddress.addressLine1') }"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                        >
                            <template #append-inner>
                                <SyncFieldIndicator :change="syncChange('addressLine1', 'foreignAddressLine1', 'foreignAddr1')" />
                            </template>
                        </v-text-field>
                    </v-col>
                    
                    <v-col cols="12">
                        <v-text-field
                            v-model="addressData.foreignAddress!.addressLine2"
                            :label="t('contact.Foreign Address Line 2') || 'Foreign Address Line 2'"
                            :readonly="isFieldReadonly('foreignAddress.addressLine2')"
                            :class="{ 'readonly-field': isFieldReadonly('foreignAddress.addressLine2') }"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                        >
                            <template #append-inner>
                                <SyncFieldIndicator :change="syncChange('addressLine2', 'foreignAddressLine2', 'foreignAddr2')" />
                            </template>
                        </v-text-field>
                    </v-col>
                </v-row>
            </template>
            </v-card-text>
        </v-expand-transition>
    </v-card>

    <ContactAddressSelectDialog
        v-if="contactCdForSelect"
        v-model="showAddressSelectDialog"
        :contact-cd="contactCdForSelect"
        filter-address-type="M04001"
        @select="onAddressCandidateSelected"
    />

    <ContactBranchAddressShowDialog
        v-model="showBranchResidentialShowDialog"
        :items="branchResidentialShowItems"
        @select="onBranchResidentialShowSelected"
    />

    <ContactAddressConsolidateDialog
        v-if="String(props.contactCd || '').trim()"
        v-model="consolidateDialogOpen"
        :contact-cd="String(props.contactCd || '').trim()"
        @consolidated="onAddressConsolidated"
    />
</template>

<style scoped lang="scss">
/* 폰트 패밀리 */
* {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.contact-address-section {
    background-color: rgb(var(--v-theme-surface)) !important;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
}

.contact-section-title {
    font-size: 18px;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    padding: 16px 24px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.contact-section-title--collapsible {
    cursor: pointer;
    user-select: none;
}

.my-address-summary {
    word-break: break-word;
}

.resync-action-btn {
    text-transform: none;
    font-weight: 600;
}

.contact-address-section :deep(.v-card-text) {
    padding: 24px !important;
}

/* Readonly 필드 스타일 (회색 배경) */
.readonly-field {
    opacity: 1 !important;
}

.readonly-field :deep(.v-field) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-input__control) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field__overlay) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(input),
.readonly-field :deep(textarea) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    cursor: not-allowed !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
}

.readonly-field :deep(.v-field--variant-outlined) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
}

.readonly-field :deep(.v-field--variant-outlined .v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
}

.readonly-field.v-text-field--disabled {
    opacity: 1 !important;
}

.readonly-field.v-text-field--disabled :deep(.v-field),
.readonly-field.v-text-field--disabled :deep(.v-field__input),
.readonly-field.v-text-field--disabled :deep(.v-input__control),
.readonly-field.v-text-field--disabled :deep(.v-field__overlay),
.readonly-field.v-text-field--disabled :deep(input),
.readonly-field.v-text-field--disabled :deep(textarea) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
    color: rgb(var(--v-theme-on-surface)) !important;
}

/* Disabled 필드 스타일 (읽기 전용) - 항상 회색 배경 */
.postal-readonly-field {
    opacity: 1 !important;
}

.postal-readonly-field :deep(.v-field) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.postal-readonly-field :deep(.v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.postal-readonly-field :deep(.v-input__control) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.postal-readonly-field :deep(.v-field__overlay) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.postal-readonly-field :deep(input),
.postal-readonly-field :deep(textarea) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    cursor: not-allowed !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
    opacity: 1 !important;
}

.postal-readonly-field :deep(.v-field--variant-outlined) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.postal-readonly-field :deep(.v-field--variant-outlined .v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.postal-readonly-field.v-text-field--disabled {
    opacity: 1 !important;
}

.postal-readonly-field.v-text-field--disabled :deep(.v-field),
.postal-readonly-field.v-text-field--disabled :deep(.v-field__input),
.postal-readonly-field.v-text-field--disabled :deep(.v-input__control),
.postal-readonly-field.v-text-field--disabled :deep(.v-field__overlay),
.postal-readonly-field.v-text-field--disabled :deep(input),
.postal-readonly-field.v-text-field--disabled :deep(textarea) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
    opacity: 1 !important;
}
</style>

